import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { Receipt, FileText, Mail, Hash, Plus, Trash2, Loader2, Calculator, Search, TrendingUp, Building2, Settings, ShoppingBag, ChevronDown, ChevronUp, CreditCard, Layers, CheckCircle2, AlertTriangle, Send, CheckCircle, Tag, Download, Archive, Clock, RefreshCw, Ban, X, Undo2, Paperclip } from 'lucide-react';
import { generateDoc, ownerToPdf, generateDocBlob } from '@/lib/generateLiquidacionPdf';
import JSZip from 'jszip';
import { api } from '@/lib/api';
import { formatDate, classNames } from '@/lib/utils';
import { MODEL_LABELS, MODEL_COLORS, PAYMENT_STATUS, docLabel, fmt, periodoCorto, saldoAFavorDeSereno, aFavorDe } from '@/lib/liquidaciones';
import OwnerLink from '@/components/owners/OwnerLink';
import PasoFacturaHolded from '@/components/liquidaciones/PasoFacturaHolded';
import PasoEmailPropietario from '@/components/liquidaciones/PasoEmailPropietario';
import PasoFacturaPropietario, { aplicaCompraHolded } from '@/components/liquidaciones/PasoFacturaPropietario';
import CambiarNumeroFactura from '@/components/liquidaciones/CambiarNumeroFactura';
import EnvioMasivo from '@/components/liquidaciones/EnvioMasivo';
import AdjuntoMasivo from '@/components/liquidaciones/AdjuntoMasivo';
import PagoMasivo from '@/components/liquidaciones/PagoMasivo';
import IconoCorreo, { IconoCorreoLiquidacion, IconoProgramado } from '@/components/liquidaciones/IconoCorreo';
import { estadoCorreo, textoProgramado } from '@/lib/correoLiquidacion';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// ─── Resumen helpers ─────────────────────────────────────────────────────────

type Categoria = 'ingresos' | 'plataforma' | 'gestion' | 'renting' | 'gasto';

function inferCategoria(label: string): Categoria {
  const l = label.toLowerCase();
  if (l.startsWith('gasto:') || l.startsWith('[')) return 'gasto';
  if (l.includes('plataforma')) return 'plataforma';
  if (l.includes('gestión') || l.includes('gestion') || l.includes('directa')) return 'gestion';
  if (l.includes('renting')) return 'renting';
  return 'ingresos';
}

function parseExpenseConcept(raw: string) {
  // Strip legacy "Gasto: " prefix from old saved records
  let c = raw.startsWith('Gasto: ') ? raw.slice(7) : raw;
  const tagMatch = c.match(/^\[([^\]]+)\]\s*(.*)/);
  if (tagMatch) {
    const tag = tagMatch[1];
    const rest = tagMatch[2];
    const propMatch = rest.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
    if (propMatch) return { tag, name: propMatch[1].trim(), property: propMatch[2] };
    return { tag, name: rest.trim(), property: null };
  }
  const propMatch = c.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  if (propMatch) return { tag: null, name: propMatch[1].trim(), property: propMatch[2] };
  return { tag: null, name: c, property: null };
}

const SECTION_CONFIG: Record<Categoria, { label: string }> = {
  ingresos:   { label: 'Ingresos' },
  plataforma: { label: 'Comisiones de plataforma' },
  gestion:    { label: 'Gestión Sereno' },
  renting:    { label: 'Servicios adicionales' },
  gasto:      { label: 'Gastos adicionales' },
};

const SECTION_ICONS: Record<Categoria, LucideIcon> = {
  ingresos:   TrendingUp,
  plataforma: Building2,
  gestion:    Settings,
  renting:    ShoppingBag,
  gasto:      Receipt,
};

const SECTION_ORDER: Categoria[] = ['ingresos', 'plataforma', 'gestion', 'renting', 'gasto'];
const SECTION_ORDER_EXPLOTACION: Categoria[] = ['ingresos', 'plataforma', 'renting', 'gasto', 'gestion'];

function ResumenConceptos({ conceptos, saldo, tipo }: { conceptos: any[]; saldo: number; tipo: string }) {
  const [collapsed, setCollapsed] = useState<Partial<Record<Categoria, boolean>>>({});
  const fmt = (v: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v);

  // Group by categoria
  const groups: Partial<Record<Categoria, any[]>> = {};
  for (const c of conceptos) {
    const cat: Categoria = c.categoria || inferCategoria(c.label);
    if (!groups[cat]) groups[cat] = [];
    groups[cat]!.push(c);
  }

  const order = tipo === 'factura' ? SECTION_ORDER_EXPLOTACION : SECTION_ORDER;
  const toggle = (cat: Categoria) => setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }));

  return (
    <div className="space-y-2">
      {order.filter(cat => groups[cat]?.length).map(cat => {
        const items = groups[cat]!;
        const Icon = SECTION_ICONS[cat];
        const cfg = SECTION_CONFIG[cat];
        const isCollapsed = collapsed[cat];
        const sectionTotal = items.reduce((s: number, c: any) => {
          if (c.total != null) return s + c.total;
          return s + (c.ing || 0) + (c.pago || 0);
        }, 0);

        // Sub-group expense items by tag
        let subGroups: { tag: string | null; label: string; items: any[] }[] | null = null;
        if (cat === 'gasto') {
          const map = new Map<string, any[]>();
          for (const item of items) {
            const { tag } = parseExpenseConcept(item.label);
            const key = tag || '__manual__';
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(item);
          }
          subGroups = Array.from(map.entries()).map(([key, its]) => ({
            tag: key === '__manual__' ? null : key,
            label: key === '__manual__' ? 'Otros' : key,
            items: its,
          }));
        }

        return (
          <div key={cat} className="rounded-xl border border-gray-200 overflow-hidden">
            {/* Section header */}
            <button
              className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-sereno-dark hover:bg-gray-100 transition-colors"
              onClick={() => toggle(cat)}
            >
              <span className="flex items-center gap-2">
                <Icon size={14} className="text-sereno-dark/60" />
                {cfg.label}
              </span>
              <span className="flex items-center gap-3">
                <span className="text-sereno-dark/70 tabular-nums">
                  {sectionTotal !== 0 ? fmt(sectionTotal) : ''}
                </span>
                {isCollapsed ? <ChevronDown size={14} className="text-sereno-dark/40" /> : <ChevronUp size={14} className="text-sereno-dark/40" />}
              </span>
            </button>

            {/* Section body */}
            {!isCollapsed && (
              <div className="bg-white divide-y divide-gray-100">
                {subGroups ? (
                  subGroups.map(sg => (
                    <div key={sg.label}>
                      {subGroups!.length > 1 && sg.tag && (
                        <div className="px-4 py-1.5 bg-gray-50/60">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-sereno-beau/30 text-sereno-dark border border-sereno-beau/50">
                            {sg.tag}
                          </span>
                        </div>
                      )}
                      {sg.items.map((item: any, i: number) => {
                        const { name, property } = parseExpenseConcept(item.label);
                        return (
                          <div key={i} className="flex justify-between items-center px-4 py-2.5 text-sm">
                            <div>
                              <span className="text-sereno-dark">{name}</span>
                              {property && <span className="ml-2 text-xs text-gray-400">({property})</span>}
                            </div>
                            <span className="text-sereno-dark/70 font-medium tabular-nums">
                              {item.pago != null && item.pago !== 0 ? fmt(item.pago) : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ))
                ) : (
                  items.map((c: any, i: number) => (
                    c.base != null ? (
                      /* Item con base/iva/irpf/total — Liquidación servicios */
                      <div key={i} className="px-4 py-3 text-sm space-y-1.5">
                        <span className="font-semibold text-sereno-dark">{c.label}</span>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-sereno-dark/70 pl-2">
                          <span>Base imponible</span>
                          <span className="text-right tabular-nums">{fmt(c.base)}</span>
                          <span>IVA (21%)</span>
                          <span className="text-right tabular-nums">{fmt(c.iva)}</span>
                          {c.irpf != null && c.irpf !== 0 && <>
                            <span>Retención IRPF (19%)</span>
                            <span className="text-right tabular-nums text-red-500">{fmt(c.irpf)}</span>
                          </>}
                          <span className="font-semibold text-sereno-dark pt-1 border-t border-gray-100">Total factura</span>
                          <span className="text-right tabular-nums font-semibold text-sereno-dark pt-1 border-t border-gray-100">{fmt(c.total)}</span>
                        </div>
                      </div>
                    ) : (
                      <div key={i} className="flex justify-between items-center px-4 py-2.5 text-sm">
                        <span className="text-sereno-dark">{c.label}</span>
                        <div className="flex gap-6 tabular-nums">
                          {c.ing != null && c.ing !== 0 && <span className="text-sereno-dark font-semibold">{fmt(c.ing)}</span>}
                          {c.pago != null && c.pago !== 0 && <span className="text-sereno-dark/70 font-medium">{fmt(c.pago)}</span>}
                        </div>
                      </div>
                    )
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Total */}
      <div className="flex justify-between items-center pt-4 px-1 border-t-2 border-sereno-dark/20 mt-1">
        <span className="text-base font-bold text-sereno-dark">
          {tipo === 'liquidacion' ? (saldo < 0 ? 'Saldo a favor de Sereno' : 'Saldo a favor del propietario') : tipo === 'factura' ? 'Total factura' : 'Total'}
        </span>
        <span className="text-xl font-bold text-sereno-dark">
          {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Math.abs(saldo))}
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 1 propietario · 2 reservas · 3 gastos · 4 resumen y guardar · 5 factura en
// Holded · 6 correo al propietario · 7 pago con Qonto
type WizardStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Blob → base64 sin el prefijo `data:…;base64,`. */
function blobABase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

interface Expense {
  concept: string;
  amount: string;
  excluded?: boolean;  // excluded from this liquidación
  docId?: string;       // DB document ID for marking as COBRADO
  source?: 'laundry' | 'maintenance';  // source container
  propertyId?: string; // partition key for DB update
}

export default function LiquidacionesPage() {
  const [view, setView] = useState<'list' | 'new' | 'detail' | 'payment'>('list');
  const [liquidaciones, setLiquidaciones] = useState<any[]>([]);
  const [owners, setOwners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLiq, setSelectedLiq] = useState<any>(null);
  const [syncingPayment, setSyncingPayment] = useState(false);
  const [cancelandoProgramado, setCancelandoProgramado] = useState(false);

  // New liquidación form state
  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [month, setMonth] = useState(new Date().getMonth()); // 0-indexed for display, +1 for API
  const [year, setYear] = useState(new Date().getFullYear());
  const [reservas, setReservas] = useState<any[]>([]);
  const [cancelledReservas, setCancelledReservas] = useState<any[]>([]);
  const [showCancelled, setShowCancelled] = useState(false);
  const [editReservas, setEditReservas] = useState(false);
  const [recomputingReservas, setRecomputingReservas] = useState(false);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [preview, setPreview] = useState<any>(null);
  const [loadingReservas, setLoadingReservas] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(0); // 0 = todos
  const [filterOwner, setFilterOwner] = useState('');
  // `por_pagar` es lo que cuenta el Dashboard: sin pago empezado y con saldo a
  // favor del propietario. `pending` es todo lo que no está pagado.
  const [filterPayment, setFilterPayment] = useState<'all' | 'paid' | 'pending' | 'por_pagar'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [archivingExpenseId, setArchivingExpenseId] = useState<string | null>(null);

  // Tabs
  const [activeTab, setActiveTab] = useState<'liquidaciones' | 'remesas' | 'pendingPricing'>('liquidaciones');
  // Remesas
  const [remesas, setRemesas] = useState<any[]>([]);
  const [deleteRemesaId, setDeleteRemesaId] = useState<string | null>(null);
  const [deleteRemesaAlsoLiqs, setDeleteRemesaAlsoLiqs] = useState(false);
  const [deletingRemesa, setDeletingRemesa] = useState(false);
  const [loadingRemesas, setLoadingRemesas] = useState(false);
  const [generatingRemesa, setGeneratingRemesa] = useState(false);
  const [showGenerateRemesa, setShowGenerateRemesa] = useState(false);
  const [remesaMonth, setRemesaMonth] = useState(() => {
    const d = new Date();
    return d.getMonth() === 0 ? 11 : d.getMonth() - 1;
  });
  const [remesaYear, setRemesaYear] = useState(() => {
    const d = new Date();
    if (d.getMonth() === 0) d.setFullYear(d.getFullYear() - 1);
    return d.getFullYear();
  });
  const [remesaFilterYear, setRemesaFilterYear] = useState(new Date().getFullYear());
  const [remesaDetail, setRemesaDetail] = useState<any>(null);
  const [loadingRemesaDetail, setLoadingRemesaDetail] = useState(false);
  // Pasos 5 a 7, sobre la liquidación ya guardada
  const [savedLiq, setSavedLiq] = useState<any>(null);
  // true si los pasos 5-7 se han abierto desde una liquidación guardada, no
  // justo después de crearla: entonces se vuelve a su ficha y no al listado
  const [pasosDesdeFicha, setPasosDesdeFicha] = useState(false);
  // Diálogo «Cambiar número» de la factura de propietario, desde la ficha
  const [cambiandoNumero, setCambiandoNumero] = useState(false);
  // Envío por correo de varias liquidaciones seleccionadas en el listado
  const [envioMasivo, setEnvioMasivo] = useState<any[] | null>(null);
  // Pago con Qonto de varias liquidaciones seleccionadas en el listado
  const [pagoMasivo, setPagoMasivo] = useState<any[] | null>(null);
  // Liquidación en PDF adjunta a la factura de Holded, de varias a la vez
  const [adjuntoMasivo, setAdjuntoMasivo] = useState<any[] | null>(null);
  // Adjuntar la liquidación desde la ficha
  const [adjuntandoFicha, setAdjuntandoFicha] = useState(false);
  const [errorAdjuntoFicha, setErrorAdjuntoFicha] = useState('');
  useEffect(() => { setErrorAdjuntoFicha(''); }, [selectedLiq?.id]);
  const [paymentConcept, setPaymentConcept] = useState('');
  const [showPayConfirm, setShowPayConfirm] = useState(false);
  // Pago a mano: alternativa a Qonto cuando se cobró por fuera
  const [markPaidTarget, setMarkPaidTarget] = useState<any[] | null>(null);
  const [markPaidConcept, setMarkPaidConcept] = useState('');
  const [markPaidDate, setMarkPaidDate] = useState('');
  const [markPaidError, setMarkPaidError] = useState('');
  const [markingPaid, setMarkingPaid] = useState(false);
  const [unmarkingPaid, setUnmarkingPaid] = useState(false);
  // Mark COBRADO dialog
  const [showMarkCobradoDialog, setShowMarkCobradoDialog] = useState(false);
  // Remesa progress
  const [remesaProgress, setRemesaProgress] = useState<{ current: number; total: number; ownerName: string } | null>(null);
  const [remesaSkippedExpenses, setRemesaSkippedExpenses] = useState(0);
  const [paying, setPaying] = useState(false);
  const [paymentDone, setPaymentDone] = useState<{ success: boolean; error?: string } | null>(null);
  // Pending pricing
  const [pendingExpenses, setPendingExpenses] = useState<any[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [savingExpenseId, setSavingExpenseId] = useState<string | null>(null);
  const [expensePrices, setExpensePrices] = useState<Record<string, string>>({});

  // La ficha del propietario enlaza aquí: ?id=… abre esa liquidación y
  // ?ownerId=… deja el listado filtrado por él. El id se quita de la URL al
  // volver al listado, para que el efecto no vuelva a abrir el detalle.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = searchParams.get('id');
  const deepLinkOwnerId = searchParams.get('ownerId');
  const deepLinkYear = Number(searchParams.get('year')) || 0;
  // Las filas del Dashboard: ?pago=pendiente (por pagar, de cualquier año) y
  // ?tab=sin-precio (los gastos sin precio)
  const deepLinkPago = searchParams.get('pago');
  const deepLinkTab = searchParams.get('tab');

  const clearDeepLink = () => {
    if (!deepLinkId) return;
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    loadData();
    // Load pending count badge on mount
    api.getPendingPricingExpenses().then(data => setPendingCount(data.length)).catch(() => {});
  }, []);

  useEffect(() => {
    if (deepLinkOwnerId) setFilterOwner(deepLinkOwnerId);
    if (deepLinkYear) setFilterYear(deepLinkYear);
  }, [deepLinkOwnerId, deepLinkYear]);

  useEffect(() => {
    if (deepLinkPago === 'pendiente') {
      setFilterPayment('por_pagar');
      if (!deepLinkYear) setFilterYear(0);
    }
    if (deepLinkTab === 'sin-precio') setActiveTab('pendingPricing');
  }, [deepLinkPago, deepLinkTab, deepLinkYear]);

  useEffect(() => {
    if (!deepLinkId || view === 'detail') return;
    const liq = liquidaciones.find(l => l.id === deepLinkId);
    if (liq) openLiqDetail(liq);
  }, [deepLinkId, liquidaciones]);

  useEffect(() => {
    if (activeTab === 'remesas') loadRemesas();
    if (activeTab === 'pendingPricing') loadPendingExpenses();
  }, [activeTab]);

  async function loadData() {
    setLoading(true);
    try {
      const [liqResult, ownResult] = await Promise.allSettled([
        api.getLiquidaciones(),
        api.getOwners(),
      ]);
      if (liqResult.status === 'fulfilled') setLiquidaciones(liqResult.value);
      else console.error('Error loading liquidaciones:', liqResult.reason);
      if (ownResult.status === 'fulfilled') setOwners(ownResult.value);
      else console.error('Error loading owners:', ownResult.reason);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadRemesas(y?: number) {
    setLoadingRemesas(true);
    try {
      const data = await api.getRemesas({ year: y ?? remesaFilterYear });
      setRemesas(data);
    } catch (err) {
      console.error('Error loading remesas:', err);
    } finally {
      setLoadingRemesas(false);
    }
  }

  async function loadPendingExpenses() {
    setLoadingPending(true);
    try {
      const data = await api.getPendingPricingExpenses();
      setPendingExpenses(data);
      setPendingCount(data.length);
    } catch (err) {
      console.error('Error loading pending expenses:', err);
    } finally {
      setLoadingPending(false);
    }
  }

  async function saveExpensePrice(expense: any) {
    const amount = parseFloat(expensePrices[expense.id] || '');
    if (!amount || amount <= 0) return;
    setSavingExpenseId(expense.id);
    try {
      await api.updateExpensePrice({ id: expense.id, source: expense.source, propertyId: expense.propertyId, amount });
      setPendingExpenses(prev => prev.filter(e => e.id !== expense.id));
      setPendingCount(prev => Math.max(0, prev - 1));
      setExpensePrices(prev => { const next = { ...prev }; delete next[expense.id]; return next; });
    } catch (err) {
      console.error('Error saving expense price:', err);
    } finally {
      setSavingExpenseId(null);
    }
  }

  async function doArchiveExpense(expense: any) {
    setArchivingExpenseId(expense.id);
    try {
      await api.archiveExpense({ id: expense.id, source: expense.source, propertyId: expense.propertyId });
      setPendingExpenses(prev => prev.filter(e => e.id !== expense.id));
      setPendingCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error archivando gasto:', err);
    } finally {
      setArchivingExpenseId(null);
    }
  }

  async function handleDownloadAll() {
    if (filteredLiquidaciones.length === 0) return;
    setDownloadingAll(true);
    try {
      const zip = new JSZip();
      for (const liq of filteredLiquidaciones) {
        const owner = owners.find((o: any) => o.id === liq.ownerId);
        if (!owner || !liq.result) continue;
        try {
          const pdfOwner = ownerToPdf(owner, liq.modelo, liq);
          const blob = generateDocBlob(pdfOwner, liq.result, liq.reservas || [], liq.periodo);
          const tipo = liq.result.tipo === 'factura' ? 'Factura' : 'Liquidacion';
          const ownerSlug = liq.ownerName.replace(/\s+/g, '_');
          const periodoSlug = liq.periodo.replace(' ', '_');
          const filename = `${tipo}_${ownerSlug}_${periodoSlug}.pdf`;
          zip.file(filename, blob);
        } catch (err) {
          console.warn(`[DownloadAll] Skipping ${liq.ownerName}: ${err}`);
        }
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      const periodoLabel = filteredLiquidaciones[0]?.periodo?.replace(' ', '_') || 'liquidaciones';
      a.download = `Liquidaciones_${periodoLabel}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err?.message || 'Error al generar el ZIP');
    } finally {
      setDownloadingAll(false);
    }
  }

  /** Descarga el PDF de una liquidación (o factura, según el modelo) */
  function downloadLiqDoc(liq: any) {
    try {
      const owner = owners.find((o: any) => o.id === liq.ownerId);
      if (!owner) {
        alert('No se encuentra el propietario de esta liquidación');
        return;
      }
      // La factura necesita serie: la guardada en la liquidación o, si no, la del propietario
      if (liq.result?.tipo === 'factura' && !liq.invoicePrefix && !owner.invoicePrefix) {
        alert('Esta factura no tiene serie. Asígnale número desde la liquidación («Cambiar número»).');
        return;
      }
      const pdfOwner = ownerToPdf(owner, liq.modelo, liq);
      generateDoc(pdfOwner, liq.result, liq.reservas || [], liq.periodo);
    } catch (err: any) {
      alert(err?.message || 'Error al generar el PDF');
    }
  }

  /**
   * Retoma los pasos 5 a 7 sobre una liquidación ya guardada: la factura o el
   * correo que se saltaron al crearla se pueden hacer después sin repetirla.
   */
  function abrirPasosGuardada(liq: any, paso: 5 | 6 | 7) {
    setSavedLiq(liq);
    setSelectedOwnerId(liq.ownerId);
    setMonth((liq.month || 1) - 1);
    setYear(liq.year);
    setPaymentConcept(`Liquidación ${liq.periodo} - ${liq.ownerName}`);
    setPaymentDone(null);
    setShowPayConfirm(false);
    setPasosDesdeFicha(true);
    setWizardStep(paso);
    setView('new');
    // Si se llegó por enlace (?id=…), el efecto que lo abre volvería a llevar
    // a la ficha en cuanto la lista cambie al crear la factura o enviar el correo
    clearDeepLink();
  }

  /** Salir del asistente: a la ficha si se entró desde ella, si no al listado. */
  function salirDelAsistente() {
    if (pasosDesdeFicha) volverAFicha();
    else { setView('list'); resetForm(); }
  }

  /** De los pasos 5-7 a la ficha de la liquidación, con lo que haya cambiado. */
  function volverAFicha() {
    const liq = liquidaciones.find(l => l.id === savedLiq?.id) || savedLiq;
    resetForm();
    setSelectedLiq(liq);
    setView('detail');
  }

  /** La liquidación guardada ha cambiado en el servidor (factura, correo…). */
  function actualizarLiqGuardada(liq: any) {
    // Solo si el asistente está con esa liquidación: desde la ficha no hay ninguna abierta
    setSavedLiq((prev: any) => (prev && prev.id === liq.id ? { ...liq, ownerName: prev.ownerName || liq.ownerName } : prev));
    setLiquidaciones(prev => prev.map(l => (l.id === liq.id ? liq : l)));
    setSelectedLiq((prev: any) => (prev && prev.id === liq.id ? liq : prev));
  }

  /** El PDF de la liquidación o de la factura de propietario, igual que al descargarlo. */
  function pdfDocBlob(liq: any): Blob {
    const owner = owners.find((o: any) => o.id === liq.ownerId);
    if (!owner) throw new Error('No se encuentra el propietario de esta liquidación');
    if (liq.result?.tipo === 'factura' && !liq.invoicePrefix && !owner.invoicePrefix) {
      throw new Error('Esta factura no tiene serie: asígnale número con «Cambiar número».');
    }
    return generateDocBlob(ownerToPdf(owner, liq.modelo, liq), liq.result, liq.reservas || [], liq.periodo);
  }

  /** El mismo PDF, en base64, para adjuntarlo al correo. */
  async function pdfLiquidacionBase64(liq: any): Promise<{ nombre: string; base64: string }> {
    const blob = pdfDocBlob(liq);
    const owner = String(liq.ownerName || '').replace(/\s+/g, '_');
    const periodo = String(liq.periodo || '').replace(' ', '_');
    const nombre = liq.result?.tipo === 'factura'
      ? `Factura_${liq.invoicePrefix ?? ''}${liq.invoiceNum ?? ''}_${owner}_${periodo}.pdf`
      : `Liquidacion_${owner}_${periodo}.pdf`;
    return { nombre, base64: await blobABase64(blob) };
  }

  /** La liquidación más reciente del propietario: la siguiente factura debería seguir a la suya. */
  function esUltimaDelPropietario(liq: any): boolean {
    const clave = (l: any) => (l.year || 0) * 12 + (l.month || 0);
    return !liquidaciones.some(l => l.ownerId === liq.ownerId && l.id !== liq.id && clave(l) > clave(liq));
  }

  /** Número de factura de propietario cambiado: liquidación y, si se pidió, propietario. */
  function aplicarNumeroCambiado(r: { liquidacion: any; owner: any | null }) {
    actualizarLiqGuardada(r.liquidacion);
    if (r.owner) setOwners(prev => prev.map(o => (o.id === r.owner.id ? r.owner : o)));
  }

  const filteredLiquidaciones = useMemo(() => {    return liquidaciones.filter(l => {
      if (filterYear && l.year !== filterYear) return false;
      if (filterMonth && l.month !== filterMonth) return false;
      if (filterOwner && l.ownerId !== filterOwner) return false;
      if (filterPayment !== 'all') {
        const paid = l.paymentStatus === 'completed';
        if (filterPayment === 'paid' && !paid) return false;
        if (filterPayment === 'pending' && paid) return false;
        if (filterPayment === 'por_pagar' && !((!l.paymentStatus || l.paymentStatus === 'none') && Number(l.result?.saldo) > 0)) return false;
      }
      return true;
    });
  }, [liquidaciones, filterYear, filterMonth, filterOwner, filterPayment]);

  const selectedOwner = useMemo(() => owners.find(o => o.id === selectedOwnerId), [owners, selectedOwnerId]);

  // El ownerName guardado en la liquidación es una foto fija: el propietario
  // puede haberse borrado desde entonces. Se enlaza solo si sigue vivo.
  const ownerIds = useMemo(() => new Set(owners.map(o => o.id)), [owners]);

  async function fetchReservations() {
    if (!selectedOwnerId) return;
    setLoadingReservas(true);
    setPreview(null);
    try {
      const [resData, expData] = await Promise.allSettled([
        api.getLiquidacionReservations(selectedOwnerId, month + 1, year),
        api.getLavanderiaExpenses(selectedOwnerId),
      ]);
      if (resData.status === 'fulfilled') {
        setReservas(resData.value.reservations || []);
        setCancelledReservas(resData.value.cancelledReservations || []);
        setShowCancelled(false);
      } else {
        console.error('Error fetching reservations:', resData.reason);
        setReservas([]);
        setCancelledReservas([]);
      }
      if (expData.status === 'fulfilled' && expData.value.expenses.length > 0) {
        setExpenses(expData.value.expenses.map((e: any) => ({
          concept: e.concept,
          amount: String(e.amount),
          docId: e.docId,
          source: e.source,
          propertyId: e.propertyId,
        })));
      }
    } catch (err: any) {
      console.error('Error fetching reservations:', err);
      setReservas([]);
    } finally {
      setLoadingReservas(false);
      setWizardStep(2);
    }
  }

  // Updates a single raw field of a reservation locally (controlled inputs)
  function updateReservaField(index: number, field: string, value: any) {
    setReservas(prev => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  }

  const editInputCls = 'text-right border border-amber-300 rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400 bg-amber-50';

  // Renders an editable numeric input in edit mode, or the formatted display otherwise
  function numCell(index: number, field: string, value: number, display: React.ReactNode, width = 'w-24') {
    if (!editReservas) return display;
    return (
      <input
        type="number"
        step="0.01"
        value={value ?? 0}
        onChange={e => updateReservaField(index, field, e.target.value === '' ? 0 : Number(e.target.value))}
        className={`${width} ${editInputCls}`}
      />
    );
  }

  // Renting cell, editable (base sin IVA) when the amount can't come from the property's
  // renting table: cancelled reservations (no linen consumed, 0 by default) and rows the
  // table can't price — guest count above the highest defined (e.g. 8 guests on a table
  // that stops at 6) or property with no renting config. The server flags them with
  // rentingEditable; the typed amount is applied on «Hecho».
  function rentingCell(index: number, r: any, display: React.ReactNode, conIva = false) {
    if (!editReservas || !(r.isCancelled || r.rentingEditable)) return display;
    return (
      <input
        type="number"
        step="0.01"
        title={r.isCancelled
          ? `Renting sin IVA — se recalcula al pulsar «Hecho»${conIva ? ' (se le suma el 21%)' : ''}`
          : `Sin precio de renting para ${r.huespedes ?? '—'} huéspedes: escríbelo a mano, sin IVA${conIva ? ' (se le suma el 21% al pulsar «Hecho»)' : ''}`}
        placeholder="0"
        value={r.rentingOverride ?? ''}
        onChange={e => updateReservaField(index, 'rentingOverride', e.target.value === '' ? 0 : Number(e.target.value))}
        className={`w-20 ${editInputCls}`}
      />
    );
  }

  const cancelledKey = (r: any) => String(r.id ?? r.codigo ?? `${r.anuncio}|${r.entrada}|${r.huesped}`);

  // Cancelled reservations already moved into the settlement set
  const addedCancelledKeys = useMemo(
    () => new Set(reservas.filter((r: any) => r.isCancelled).map(cancelledKey)),
    [reservas],
  );

  function addCancelledReserva(c: any) {
    setReservas(prev => (prev.some((r: any) => r.isCancelled && cancelledKey(r) === cancelledKey(c)) ? prev : [...prev, { ...c }]));
    setPreview(null);
  }

  function removeCancelledReserva(c: any) {
    setReservas(prev => prev.filter((r: any) => !(r.isCancelled && cancelledKey(r) === cancelledKey(c))));
    setPreview(null);
  }

  // Con penalización cobrada (cancellation_fee > 0) = dinero que el huésped perdió
  // y debe llegar al propietario. El resto son cancelaciones sin cargo.
  const cancelledConPenalizacion = useMemo(() => cancelledReservas.filter((c: any) => c.hasPenalty), [cancelledReservas]);
  const cancelledSinPenalizacion = useMemo(() => cancelledReservas.filter((c: any) => !c.hasPenalty), [cancelledReservas]);
  const cancelledAñadidas = useMemo(
    () => cancelledReservas.filter((c: any) => addedCancelledKeys.has(cancelledKey(c))).length,
    [cancelledReservas, addedCancelledKeys],
  );

  // Tabla (desktop) + tarjetas (móvil) de reservas canceladas, con botón para
  // moverlas al conjunto que se liquida arriba.
  function renderCancelledList(list: any[], highlight: boolean) {
    const addBtn = (c: any) => {
      const added = addedCancelledKeys.has(cancelledKey(c));
      return added ? (
        <button
          onClick={() => removeCancelledReserva(c)}
          className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-gray-300 bg-white text-sereno-dark/60 hover:bg-gray-100 inline-flex items-center gap-1 whitespace-nowrap"
        >
          <X size={12} /> Quitar
        </button>
      ) : (
        <button
          onClick={() => addCancelledReserva(c)}
          className={`text-xs font-semibold px-2.5 py-1 rounded-lg inline-flex items-center gap-1 whitespace-nowrap ${highlight ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-sereno-dark text-white hover:opacity-90'}`}
        >
          <Plus size={12} /> Añadir
        </button>
      );
    };

    return (
      <>
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={highlight ? 'bg-amber-200 text-amber-900' : 'bg-gray-100 text-sereno-dark/70'}>
                <th className="text-left py-2 px-3 rounded-tl-lg font-semibold">Propiedad</th>
                <th className="text-left py-2 px-3 font-semibold">Huésped</th>
                <th className="text-left py-2 px-3 font-semibold">Estancia</th>
                <th className="text-right py-2 px-3 font-semibold">Huésp.</th>
                <th className="text-left py-2 px-3 font-semibold">Canal</th>
                <th className="text-left py-2 px-3 font-semibold">Cancelada</th>
                <th className="text-right py-2 px-3 font-semibold">Total reserva</th>
                <th className="text-right py-2 px-3 font-semibold">Penalización</th>
                <th className="py-2 px-3 rounded-tr-lg"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((c: any, i: number) => {
                const added = addedCancelledKeys.has(cancelledKey(c));
                const fee = Number(c.cancellationFee) || 0;
                return (
                  <tr
                    key={cancelledKey(c)}
                    className={added ? 'bg-green-50' : highlight ? (i % 2 === 0 ? 'bg-white' : 'bg-amber-50') : (i % 2 === 0 ? '' : 'bg-gray-50')}
                  >
                    <td className="py-2 px-3 font-medium">{c.anuncio}</td>
                    <td className="py-2 px-3 capitalize">
                      {c.huesped}
                      {added && (
                        <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold normal-case bg-green-100 text-green-700">en la liquidación</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-sereno-dark/70 whitespace-nowrap">{(c.entrada || '').substring(0, 10)} → {(c.salida || '').substring(0, 10)}</td>
                    <td className="py-2 px-3 text-right text-sereno-dark/50">{c.huespedes ?? '—'}</td>
                    <td className="py-2 px-3">{c.canal}</td>
                    <td className="py-2 px-3 whitespace-nowrap" title={c.cancelStatus || ''}>{(c.cancelledAt || '').substring(0, 10) || '—'}</td>
                    <td className="py-2 px-3 text-right text-sereno-dark/60">{fmt(c.precioOriginal ?? 0)}</td>
                    <td className={`py-2 px-3 text-right font-semibold ${fee > 0 ? 'text-amber-700' : 'text-sereno-dark/40'}`}>{fmt(fee)}</td>
                    <td className="py-2 px-3 text-right">{addBtn(c)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="sm:hidden space-y-2">
          {list.map((c: any) => {
            const added = addedCancelledKeys.has(cancelledKey(c));
            const fee = Number(c.cancellationFee) || 0;
            return (
              <div key={cancelledKey(c)} className={`rounded-lg p-3 space-y-1.5 ${added ? 'bg-green-50 border border-green-200' : highlight ? 'bg-white border border-amber-200' : 'bg-gray-50'}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-sereno-dark text-sm">{c.anuncio}</p>
                  <p className={`font-semibold text-sm shrink-0 ${fee > 0 ? 'text-amber-700' : 'text-sereno-dark/40'}`}>{fmt(fee)}</p>
                </div>
                <p className="text-xs text-sereno-dark/70 capitalize">
                  {c.huesped}
                  {added && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold normal-case bg-green-100 text-green-700">en la liquidación</span>
                  )}
                </p>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-sereno-dark/60">
                  <span>{(c.entrada || '').substring(0, 10)} → {(c.salida || '').substring(0, 10)}</span>
                  <span className="capitalize">{c.canal}</span>
                  <span>Cancelada: {(c.cancelledAt || '').substring(0, 10) || '—'}</span>
                  <span>Total: {fmt(c.precioOriginal ?? 0)}</span>
                </div>
                <div className="pt-1">{addBtn(c)}</div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  // Re-applies server-side enrichment after manual edits so derived columns stay consistent
  async function recomputeReservas() {
    if (!selectedOwnerId || reservas.length === 0) return;
    setRecomputingReservas(true);
    try {
      const data = await api.recomputeReservations(selectedOwnerId, reservas);
      setReservas(data.reservations || []);
    } catch (err: any) {
      console.error('Error recomputing reservations:', err);
    } finally {
      setRecomputingReservas(false);
    }
  }

  async function doPreview() {
    if (!selectedOwnerId) return;
    setLoadingPreview(true);
    try {
      const data = await api.previewLiquidacion({
        ownerId: selectedOwnerId,
        month: month + 1,
        year,
        reservas,
        expenses: expenses.filter(e => !e.excluded && e.concept && e.amount).map(e => ({ concept: e.concept, amount: Number(e.amount) })),
      });
      setPreview(data);
      setWizardStep(4);
    } catch (err: any) {
      console.error('Error previewing:', err);
    } finally {
      setLoadingPreview(false);
    }
  }

  function handleSaveClick() {
    const lavanderiaExpenses = expenses.filter(e => !e.excluded && e.docId);
    if (lavanderiaExpenses.length > 0) {
      setShowMarkCobradoDialog(true);
    } else {
      performSave(false);
    }
  }

  async function performSave(markExpensesPaid: boolean) {
    if (!selectedOwnerId || !preview) return;
    setSaving(true);
    setShowMarkCobradoDialog(false);
    try {
      const expenseIds = markExpensesPaid
        ? expenses
            .filter(e => !e.excluded && e.docId && e.source && e.propertyId)
            .map(e => ({ id: e.docId!, source: e.source!, propertyId: e.propertyId! }))
        : [];
      const created = await api.createLiquidacion({
        ownerId: selectedOwnerId,
        month: month + 1,
        year,
        reservas,
        expenses: expenses.filter(e => !e.excluded && e.concept && e.amount).map(e => ({ concept: e.concept, amount: Number(e.amount) })),
        markExpensesPaid,
        expenseIds,
      });
      await loadData();
      const ownerName = `${selectedOwner?.firstName || ''} ${selectedOwner?.lastName || ''}`.trim();
      setSavedLiq({ ...created, ownerName });
      setPaymentConcept(`Liquidación ${MONTHS[month]} ${year} - ${ownerName}`);
      setPaymentDone(null);
      setWizardStep(5);
    } catch (err: any) {
      console.error('Error saving:', err);
    } finally {
      setSaving(false);
    }
  }

  async function doDelete(id: string) {
    if (!confirm('¿Eliminar esta liquidación?')) return;
    try {
      await api.deleteLiquidacion(id);
      setLiquidaciones(prev => prev.filter(l => l.id !== id));
      if (selectedLiq?.id === id) {
        setSelectedLiq(null);
        setView('list');
      }
    } catch (err: any) {
      console.error('Error deleting:', err);
    }
  }

  async function doDeleteBulk() {
    if (!confirm(`¿Eliminar ${selectedIds.size} liquidación${selectedIds.size !== 1 ? 'es' : ''}?`)) return;
    setDeletingBulk(true);
    try {
      await Promise.all([...selectedIds].map(id => api.deleteLiquidacion(id)));
      setLiquidaciones(prev => prev.filter(l => !selectedIds.has(l.id)));
      if (selectedLiq && selectedIds.has(selectedLiq.id)) {
        setSelectedLiq(null);
        setView('list');
      }
      setSelectedIds(new Set());
    } catch (err: any) {
      console.error('Error bulk deleting:', err);
    } finally {
      setDeletingBulk(false);
    }
  }

  // --- Pago a mano ---
  // La comprobación con Qonto no ve los pagos hechos por fuera. Marcarlos a mano
  // es la alternativa, y solo desde «Pendiente»: lo que ya tiene transferencia
  // de Qonto lo gobierna la conciliación.
  const estaPendiente = (liq: any) => (liq?.paymentStatus || 'none') === 'none';

  function aplicarLiqActualizada(liq: any) {
    setLiquidaciones(prev => prev.map(l => (l.id === liq.id ? liq : l)));
    setSelectedLiq((prev: any) => (prev && prev.id === liq.id ? liq : prev));
  }

  async function cancelarCorreoProgramado(liq: any) {
    setCancelandoProgramado(true);
    try {
      const r = await api.cancelarCorreoProgramado(liq.id);
      aplicarLiqActualizada(r.liquidacion);
    } catch (err: any) {
      alert(err?.message || 'No se pudo cancelar el correo programado');
    } finally {
      setCancelandoProgramado(false);
    }
  }

  function abrirMarcarPagada(liqs: any[]) {
    const pendientes = liqs.filter(estaPendiente);
    if (pendientes.length === 0) return;
    setMarkPaidTarget(pendientes);
    setMarkPaidConcept(
      pendientes.length === 1
        ? `Pago liquidación ${pendientes[0].periodo} - ${pendientes[0].ownerName}`
        : '',
    );
    // Por defecto hoy, pero se marca días después de pagar: la fecha real importa.
    setMarkPaidDate(new Date().toISOString().slice(0, 10));
    setMarkPaidError('');
  }

  async function doMarkPaid() {
    if (!markPaidTarget) return;
    setMarkingPaid(true);
    setMarkPaidError('');
    const concept = markPaidConcept.trim();
    const date = markPaidDate || undefined;
    const fallos: string[] = [];

    // En serie: son pocas y así un 409 de una no cancela el resto.
    for (const liq of markPaidTarget) {
      try {
        const r = await api.markLiquidacionPaid(liq.id, { concept: concept || undefined, date });
        if (r?.liquidacion) aplicarLiqActualizada(r.liquidacion);
      } catch (err: any) {
        fallos.push(`${liq.ownerName} (${liq.periodo}): ${err?.message || 'error'}`);
      }
    }

    setMarkingPaid(false);
    if (fallos.length > 0) {
      setMarkPaidError(fallos.join(' · '));
      return;
    }
    setSelectedIds(new Set());
    setMarkPaidTarget(null);
  }

  async function doUnmarkPaid(liq: any) {
    if (!confirm('¿Desmarcar el pago y devolverla a Pendiente?')) return;
    setUnmarkingPaid(true);
    try {
      const r = await api.unmarkLiquidacionPaid(liq.id);
      if (r?.liquidacion) aplicarLiqActualizada(r.liquidacion);
    } catch (err: any) {
      alert(err?.message || 'No se pudo desmarcar el pago');
    } finally {
      setUnmarkingPaid(false);
    }
  }

  function resetForm() {
    setSelectedOwnerId('');
    setMonth(new Date().getMonth());
    setYear(new Date().getFullYear());
    setReservas([]);
    setCancelledReservas([]);
    setShowCancelled(false);
    setExpenses([]);
    setPreview(null);
    setSavedLiq(null);
    setPaymentConcept('');
    setPaymentDone(null);
    setShowPayConfirm(false);
    setPasosDesdeFicha(false);
    setWizardStep(1);
  }

  function addExpense() {
    setExpenses([...expenses, { concept: '', amount: '' }]);
  }

  function toggleExpenseExcluded(idx: number) {
    setExpenses(expenses.map((e, i) => i === idx ? { ...e, excluded: !e.excluded } : e));
  }

  function removeExpense(idx: number) {
    setExpenses(expenses.filter((_, i) => i !== idx));
  }

  function updateExpense(idx: number, field: 'concept' | 'amount', value: string) {
    setExpenses(expenses.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  }

  function openQontoAuthPopup(): Promise<void> {
    return new Promise((resolve, reject) => {
      const popup = window.open(
        '/api/qonto/auth',
        'qonto-auth',
        'width=620,height=720,left=200,top=80',
      );
      if (!popup) {
        reject(new Error('No se pudo abrir la ventana de autorización. Permite popups para este sitio.'));
        return;
      }
      const timer = setInterval(() => {
        if (popup.closed) {
          clearInterval(timer);
          resolve();
        }
      }, 500);
    });
  }

  async function openLiqDetail(liq: any) {
    setSelectedLiq(liq);
    setView('detail');
    // Sync Qonto status if the payment is still in flight
    if (liq.paymentQontoId && (liq.paymentStatus === 'processing' || liq.paymentStatus === 'pending')) {
      setSyncingPayment(true);
      try {
        const result: any = await api.getLiquidacionPaymentStatus(liq.id);
        const updatedLiq = result?.liquidacion;
        if (updatedLiq) {
          setSelectedLiq((prev: any) => prev && prev.id === updatedLiq.id ? updatedLiq : prev);
          setLiquidaciones(prev => prev.map(l => l.id === updatedLiq.id ? updatedLiq : l));
        }
      } catch {
        // ignore sync errors
      } finally {
        setSyncingPayment(false);
      }
    }
  }

  async function doPayLiquidacion() {
    if (!savedLiq) return;
    setPaying(true);
    setShowPayConfirm(false);

    try {
      const result: any = await api.payLiquidacion(savedLiq.id, paymentConcept);
      const updatedLiq = result?.liquidacion;
      if (updatedLiq) {
        setSavedLiq(updatedLiq);
        setSelectedLiq((prev: any) => prev && prev.id === updatedLiq.id ? updatedLiq : prev);
        setLiquidaciones(prev => prev.map(l => l.id === updatedLiq.id ? updatedLiq : l));
      }
      setPaymentDone({ success: true });
      setPaying(false);
      return;
    } catch (err: any) {
      const msg: string = err.message || '';
      // Qonto not authorized yet → open popup, then retry
      if (msg.includes('Qonto no conectado') || msg.includes('no conectado')) {
        setPaying(false);
        try {
          await openQontoAuthPopup();
        } catch (popupErr: any) {
          setPaymentDone({ success: false, error: popupErr.message });
          return;
        }
        // Retry after successful auth
        setPaying(true);
        try {
          const result: any = await api.payLiquidacion(savedLiq.id, paymentConcept);
          const updatedLiq = result?.liquidacion;
          if (updatedLiq) {
            setSavedLiq(updatedLiq);
            setSelectedLiq((prev: any) => prev && prev.id === updatedLiq.id ? updatedLiq : prev);
            setLiquidaciones(prev => prev.map(l => l.id === updatedLiq.id ? updatedLiq : l));
          }
          setPaymentDone({ success: true });
        } catch (retryErr: any) {
          setPaymentDone({ success: false, error: retryErr.message || 'Error al procesar el pago' });
        }
        setPaying(false);
        return;
      }
      setPaymentDone({ success: false, error: msg || 'Error al procesar el pago' });
      setPaying(false);
    }
  }

  async function doGenerateRemesa() {
    setGeneratingRemesa(true);
    setRemesaProgress(null);
    setRemesaSkippedExpenses(0);
    try {
      await api.generateRemesaSSE(
        { month: remesaMonth + 1, year: remesaYear },
        (info) => {
          setRemesaProgress({ current: info.current, total: info.total, ownerName: info.ownerName });
          if (info.skippedExpenses) setRemesaSkippedExpenses(prev => prev + (info.skippedExpenses ?? 0));
        },
      );
      setShowGenerateRemesa(false);
      setRemesaProgress(null);
      await loadRemesas();
      // Refresh pending count after generation
      api.getPendingPricingExpenses().then(data => setPendingCount(data.length)).catch(() => {});
    } catch (err: any) {
      console.error('Error generating remesa:', err);
      setRemesaProgress(null);
    } finally {
      setGeneratingRemesa(false);
    }
  }

  async function openRemesaDetail(id: string) {
    setLoadingRemesaDetail(true);
    setRemesaDetail(null);
    try {
      const data = await api.getRemesa(id);
      setRemesaDetail(data);
    } catch (err) {
      console.error('Error loading remesa detail:', err);
    } finally {
      setLoadingRemesaDetail(false);
    }
  }

  async function doDeleteRemesa() {
    if (!deleteRemesaId) return;
    setDeletingRemesa(true);
    try {
      await api.deleteRemesa(deleteRemesaId, deleteRemesaAlsoLiqs);
      setRemesas(prev => prev.filter(r => r.id !== deleteRemesaId));
      if (deleteRemesaAlsoLiqs) setLiquidaciones(prev => prev.filter(l => l.remesaId !== deleteRemesaId));
      if (remesaDetail?.id === deleteRemesaId) setRemesaDetail(null);
      setDeleteRemesaId(null);
      setDeleteRemesaAlsoLiqs(false);
    } catch (err: any) {
      console.error('Error deleting remesa:', err);
    } finally {
      setDeletingRemesa(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-sereno-dark/40" size={32} />
      </div>
    );
  }

  // Se abre desde el detalle y desde la lista, así que se monta en las dos.
  const markPaidModal = markPaidTarget && (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 shadow-xl max-w-md w-full space-y-4">
        <h3 className="text-lg font-bold text-sereno-dark">
          Marcar como pagada{markPaidTarget.length !== 1 ? 's' : ''}
        </h3>
        <p className="text-sm text-sereno-dark/70">
          {markPaidTarget.length === 1 ? (
            <>
              Se registrará el pago de <strong>{fmt(Math.abs(markPaidTarget[0].result?.saldo || 0))}</strong> a{' '}
              <strong>{markPaidTarget[0].ownerName}</strong> ({markPaidTarget[0].periodo}).
            </>
          ) : (
            <>
              Se registrará el pago de <strong>{markPaidTarget.length} liquidaciones</strong>, por un total de{' '}
              <strong>{fmt(markPaidTarget.reduce((s, l) => s + Math.abs(l.result?.saldo || 0), 0))}</strong>.
            </>
          )}
        </p>
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          No se envía ninguna transferencia: esto solo deja constancia de un pago ya hecho fuera de Qonto.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-sereno-dark/60 mb-1">Fecha del pago</label>
            <input
              type="date"
              className="input w-full"
              value={markPaidDate}
              onChange={e => setMarkPaidDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-sereno-dark/60 mb-1">Concepto (opcional)</label>
            <input
              type="text"
              className="input w-full"
              placeholder="Transferencia desde otra cuenta, compensación…"
              value={markPaidConcept}
              onChange={e => setMarkPaidConcept(e.target.value)}
            />
          </div>
        </div>

        {markPaidError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{markPaidError}</p>
        )}

        <div className="flex gap-3 pt-2">
          <button onClick={() => setMarkPaidTarget(null)} disabled={markingPaid} className="btn-ghost flex-1 disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={doMarkPaid} disabled={markingPaid} className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
            {markingPaid ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
            Marcar como pagada{markPaidTarget.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );

  // --- PAYMENT VIEW (step 7) ---
  if (view === 'payment' && savedLiq) {
    const ibanOwner = selectedOwner?.iban || owners.find(o => o.id === savedLiq.ownerId)?.iban;
    const canPay = !!ibanOwner && !paymentDone?.success;
    return (
      <div className="space-y-6">
        <div>
          <button onClick={() => { setView('list'); resetForm(); }} className="text-sm text-sereno-dark/50 hover:text-sereno-dark mb-1">
            ← Volver al listado
          </button>
          <h1 className="text-2xl font-bold text-sereno-dark">Liquidación guardada</h1>
          <p className="text-sereno-dark/60">
            <OwnerLink ownerId={savedLiq.ownerId} name={savedLiq.ownerName} exists={ownerIds.has(savedLiq.ownerId)} /> · {savedLiq.periodo}
          </p>
        </div>

        {/* Summary */}
        <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-sereno-dark">Resumen</h2>
            <span className="text-2xl font-bold text-sereno-dark">{fmt(Math.abs(savedLiq.result?.saldo || 0))}</span>
          </div>
          <p className={classNames('text-sm', saldoAFavorDeSereno(savedLiq.result?.saldo) ? 'text-amber-700 font-medium' : 'text-sereno-dark/60')}>{aFavorDe(savedLiq.result?.saldo)}</p>
        </div>

        {/* Payment step */}
        <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-semibold text-sereno-dark">7. Pago con Qonto</h2>
          {paymentDone?.success ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <CheckCircle2 className="text-green-500" size={48} />
              <p className="text-lg font-semibold text-sereno-dark">Transferencia enviada</p>
              <p className="text-sm text-sereno-dark/60">El pago se está procesando. Puedes verificar el estado en Qonto.</p>
              <button onClick={() => { setView('list'); resetForm(); }} className="btn-primary mt-2">
                Volver al listado
              </button>
            </div>
          ) : paymentDone && !paymentDone.success ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <AlertTriangle className="text-red-500" size={48} />
              <p className="text-lg font-semibold text-sereno-dark">Error en el pago</p>
              <p className="text-sm text-red-600">{paymentDone.error}</p>
              <button onClick={() => setPaymentDone(null)} className="btn-primary mt-2">
                Reintentar
              </button>
            </div>
          ) : !(Number(savedLiq.result?.saldo) > 0) ? (
                <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>
                    {saldoAFavorDeSereno(savedLiq.result?.saldo)
                      ? <>El saldo es <strong>a favor de Sereno</strong>: el propietario debe {fmt(Math.abs(Number(savedLiq.result?.saldo)))}, que van en la factura de Sereno. No hay nada que transferirle.</>
                      : 'El saldo es cero: no hay nada que transferir al propietario.'}
                  </span>
                  <button onClick={() => { setView('list'); resetForm(); }} className="btn-ghost text-sm ml-auto whitespace-nowrap">Volver al listado</button>
                </div>
              ) : (
            <>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">IBAN del propietario</label>
                  {ibanOwner ? (
                    <div className="input bg-gray-50 text-sereno-dark/80 font-mono text-sm">{ibanOwner}</div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <AlertTriangle size={16} />
                      Este propietario no tiene IBAN configurado. Edítalo en su ficha antes de pagar.
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Concepto de la transferencia</label>
                  <input
                    className="input"
                    value={paymentConcept}
                    onChange={e => setPaymentConcept(e.target.value)}
                    maxLength={140}
                    placeholder="Concepto de la transferencia"
                  />
                  <p className="text-xs text-sereno-dark/40 mt-1">{paymentConcept.length}/140 caracteres</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                <button
                  onClick={() => setShowPayConfirm(true)}
                  disabled={!canPay || paying}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  {paying ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                  Pagar con Qonto
                </button>
                <button
                  onClick={() => { setView('list'); resetForm(); }}
                  className="btn-ghost text-sm"
                >
                  Omitir por ahora
                </button>
              </div>
            </>
          )}
        </div>

        {/* Confirm modal */}
        {showPayConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full space-y-4">
              <h3 className="text-lg font-bold text-sereno-dark">Confirmar transferencia</h3>
              <p className="text-sm text-sereno-dark/70">
                Se enviará <strong>{fmt(Math.abs(savedLiq.result?.saldo || 0))}</strong> a <strong>{savedLiq.ownerName}</strong>.<br />
                IBAN: <span className="font-mono text-xs">{ibanOwner}</span><br />
                Concepto: {paymentConcept}
              </p>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowPayConfirm(false)} className="btn-ghost flex-1">Cancelar</button>
                <button onClick={doPayLiquidacion} className="btn-primary flex-1 flex items-center justify-center gap-2">
                  <Send size={16} /> Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- DETAIL VIEW ---
  if (view === 'detail' && selectedLiq) {
    const r = selectedLiq.result;
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => { setView('list'); setSelectedLiq(null); clearDeepLink(); }} className="text-sm text-sereno-dark/50 hover:text-sereno-dark mb-1">
              ← Volver al listado
            </button>
            <h1 className="text-2xl font-bold text-sereno-dark">{selectedLiq.periodo}</h1>
            <p className="text-sereno-dark/60">
              <OwnerLink ownerId={selectedLiq.ownerId} name={selectedLiq.ownerName} exists={ownerIds.has(selectedLiq.ownerId)} />
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={classNames('inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border', MODEL_COLORS[selectedLiq.modelo] || 'bg-gray-50 text-gray-700 border-gray-200')}>
              {MODEL_LABELS[selectedLiq.modelo] || selectedLiq.modelo}
            </span>
            <button
              onClick={() => {
                setDownloadingPdf(true);
                try {
                  downloadLiqDoc(selectedLiq);
                } finally {
                  setDownloadingPdf(false);
                }
              }}
              disabled={downloadingPdf}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sereno-dark text-white text-xs font-semibold hover:bg-sereno-dark/90 disabled:opacity-50 transition-colors"
            >
              {downloadingPdf ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {selectedLiq.result?.tipo === 'factura' ? 'Generar factura' : 'Generar liquidación'}
            </button>
          </div>
        </div>

        {/* Result summary */}
        <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-sereno-dark mb-4">Resumen</h2>
          <ResumenConceptos conceptos={r.conceptos || []} saldo={r.saldo} tipo={r.tipo} />
        </div>

        {/* Reservations detail */}
        {selectedLiq.reservas?.length > 0 && (
          <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-sereno-dark mb-4">Reservas ({selectedLiq.reservas.length})</h2>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-sereno-dark text-white">
                    <th className="text-left py-2 px-3 rounded-tl-lg">Propiedad</th>
                    <th className="text-left py-2 px-3">Huésped</th>
                    <th className="text-left py-2 px-3">Entrada</th>
                    <th className="text-left py-2 px-3">Salida</th>
                    <th className="text-left py-2 px-3">Canal</th>
                    <th className="text-right py-2 px-3">Total</th>
                    <th className="text-right py-2 px-3">Limpieza</th>
                    <th className="text-right py-2 px-3">Com. canal</th>
                    <th className="text-right py-2 px-3">Renting</th>
                    <th className="text-right py-2 px-3 rounded-tr-lg">Huésp.</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedLiq.reservas.map((res: any, i: number) => (
                    <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                      <td className="py-2 px-3 font-medium">{res.anuncio}</td>
                      <td className="py-2 px-3 capitalize">{res.huesped}</td>
                      <td className="py-2 px-3">{res.entrada}</td>
                      <td className="py-2 px-3">{res.salida}</td>
                      <td className="py-2 px-3">{res.canal}</td>
                      <td className="py-2 px-3 text-right text-green-700 font-medium">{fmt(res.precioTotal)}</td>
                      <td className="py-2 px-3 text-right">{fmt(res.limpieza)}</td>
                      <td className="py-2 px-3 text-right">{fmt((res.comisionCanal || 0) + (res.tarifaTransaccion || 0))}</td>
                      <td className="py-2 px-3 text-right">{res.renting != null ? fmt(res.renting) : '—'}</td>
                      <td className="py-2 px-3 text-right text-sereno-dark/50">{res.huespedes ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="sm:hidden space-y-2">
              {selectedLiq.reservas.map((res: any, i: number) => (
                <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-sereno-dark text-sm">{res.anuncio}</p>
                    <p className="text-green-700 font-semibold text-sm shrink-0">{fmt(res.precioTotal)}</p>
                  </div>
                  <p className="text-xs text-sereno-dark/70 capitalize">{res.huesped}</p>
                  <div className="flex items-center gap-3 text-xs text-sereno-dark/60">
                    <span>{res.entrada} → {res.salida}</span>
                    <span className="capitalize">{res.canal}</span>
                    {res.huespedes != null && <span>{res.huespedes} pers.</span>}
                  </div>
                  <div className="flex gap-4 text-xs text-sereno-dark/60 pt-0.5">
                    <span>Limpieza: {fmt(res.limpieza)}</span>
                    <span>Com. canal: {fmt((res.comisionCanal || 0) + (res.tarifaTransaccion || 0))}</span>
                    {res.renting != null && res.renting > 0 && <span>Renting: {fmt(res.renting)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Factura en Holded y correo al propietario (pasos 5 y 6) */}
        {(() => {
          const esGestion = (selectedLiq.modelo || 'gestion') === 'gestion' && r?.tipo === 'liquidacion';
          // En explotación la liquidación es la factura que el CRM emite en nombre del propietario
          const esFacturaProp = r?.tipo === 'factura';
          const numeroProp = selectedLiq.invoiceNum != null ? `${selectedLiq.invoicePrefix ?? ''}${selectedLiq.invoiceNum}` : null;
          const factura = selectedLiq.holdedInvoice;
          const envio = [...(selectedLiq.emailsEnviados || [])].reverse().find((e: any) => e.estado === 'enviado');
          const programado = selectedLiq.correoProgramado || null;
          const fecha = (iso: string) => new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
          return (
            <div className="bg-white border border-gray-100 rounded-xl shadow-sm divide-y divide-gray-100">
              {esFacturaProp ? (
              <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <FileText className={classNames('shrink-0', numeroProp ? 'text-green-500' : 'text-sereno-dark/30')} size={26} />
                  <div>
                    <p className="font-semibold text-sereno-dark">Factura del propietario</p>
                    <p className="text-sm text-sereno-dark/50 mt-0.5">
                      {numeroProp
                        ? <>Nº <span className="font-mono">{numeroProp}</span>{r?.total != null ? ` · ${fmt(r.total)}` : ''} · la emite el CRM en su nombre a Sereno</>
                        : 'Sin número de factura: asígnale uno para poder generarla'}
                    </p>
                    {aplicaCompraHolded(selectedLiq) && (() => {
                      const compra = selectedLiq.holdedPurchase;
                      const completa = compra && compra.adjunto && !compra.draft;
                      return (
                        <p className={classNames('text-sm mt-0.5', completa ? 'text-green-700' : 'text-amber-700')}>
                          {compra
                            ? `En Holded: compra ${compra.numero || numeroProp || ''} · ${compra.draft ? 'en borrador' : 'aprobada'}${compra.adjunto ? '' : ' · sin el PDF adjunto'}${compra.cuenta === 'test' ? ' · cuenta de pruebas' : ''}`
                            : 'En Holded: todavía sin registrar como compra'}
                        </p>
                      );
                    })()}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setCambiandoNumero(true)}
                    disabled={Boolean(selectedLiq.holdedPurchase)}
                    title={selectedLiq.holdedPurchase ? 'Ya está registrada en Holded con este número' : undefined}
                    className="btn-ghost text-sm flex items-center gap-2 disabled:opacity-40"
                  >
                    <Hash size={16} /> {numeroProp ? 'Cambiar número' : 'Asignar número'}
                  </button>
                  {aplicaCompraHolded(selectedLiq) && !selectedLiq.holdedPurchase ? (
                    <button onClick={() => abrirPasosGuardada(selectedLiq, 5)} disabled={!numeroProp} className="btn-primary flex items-center gap-2">
                      <FileText size={16} /> Registrar en Holded
                    </button>
                  ) : (
                    <button onClick={() => abrirPasosGuardada(selectedLiq, 5)} disabled={!numeroProp} className="btn-ghost text-sm flex items-center gap-2">
                      <FileText size={16} /> Ver factura
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <FileText className={classNames('shrink-0', factura ? 'text-green-500' : 'text-sereno-dark/30')} size={26} />
                  <div>
                    <p className="font-semibold text-sereno-dark">Factura en Holded</p>
                    <p className="text-sm text-sereno-dark/50 mt-0.5">
                      {!esGestion
                        ? 'No aplica: solo se factura en el modelo de gestión'
                        : factura
                          ? `${factura.draft ? 'Borrador, sin aprobar' : `Factura ${factura.docNumber || ''}`}${factura.total != null ? ` · ${fmt(factura.total)}` : ''} · creada el ${fecha(factura.createdAt)}${factura.cuenta === 'test' ? ' · cuenta de pruebas' : ''}`
                          : 'Todavía sin factura'}
                    </p>
                    {esGestion && factura && (
                      <p className={classNames('text-sm mt-0.5 flex items-center gap-1.5', factura.adjunto ? 'text-green-700' : 'text-amber-700')}>
                        <Paperclip size={13} />
                        {factura.adjunto ? 'Liquidación adjunta en Holded' : 'Sin la liquidación adjunta en Holded'}
                      </p>
                    )}
                    {errorAdjuntoFicha && <p className="text-sm mt-0.5 text-red-600">{errorAdjuntoFicha}</p>}
                  </div>
                </div>
                {esGestion && (
                  <div className="flex items-center gap-2 shrink-0">
                    {factura && !factura.adjunto && (
                      <button
                        onClick={async () => {
                          setAdjuntandoFicha(true);
                          setErrorAdjuntoFicha('');
                          try {
                            const res = await api.adjuntarLiquidacionHolded(selectedLiq.id, await pdfLiquidacionBase64(selectedLiq));
                            aplicarLiqActualizada(res.liquidacion);
                          } catch (err: any) {
                            setErrorAdjuntoFicha(err?.message || 'No se pudo adjuntar la liquidación');
                          } finally {
                            setAdjuntandoFicha(false);
                          }
                        }}
                        disabled={adjuntandoFicha}
                        className="btn-ghost text-sm flex items-center gap-2"
                      >
                        {adjuntandoFicha ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />} Adjuntar liquidación
                      </button>
                    )}
                    {factura && (
                      <a href={api.urlPdfFacturaHolded(selectedLiq.id)} target="_blank" rel="noreferrer" className="btn-ghost text-sm flex items-center gap-2">
                        <Download size={16} /> Ver PDF
                      </a>
                    )}
                    <button onClick={() => abrirPasosGuardada(selectedLiq, 5)} className={classNames(factura && !factura.draft ? 'btn-ghost text-sm' : 'btn-primary', 'flex items-center gap-2')}>
                      <FileText size={16} /> {!factura ? 'Crear factura' : factura.draft ? 'Revisar y aprobar' : 'Abrir paso 5'}
                    </button>
                  </div>
                )}
              </div>
            )}
              <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {programado ? <IconoProgramado programado={programado} size={26} className="shrink-0" /> : envio ? <IconoCorreo envio={envio} size={26} className="shrink-0" /> : <Mail className="shrink-0 text-sereno-dark/30" size={26} />}
                  <div>
                    <p className="font-semibold text-sereno-dark">Correo al propietario</p>
                    <p className="text-sm text-sereno-dark/50 mt-0.5">
                      {envio
                        ? `Enviado el ${fecha(envio.fecha)} a ${envio.para}${envio.adjuntos?.length ? ` · ${envio.adjuntos.length} adjunto${envio.adjuntos.length > 1 ? 's' : ''}` : ''}`
                        : 'Todavía sin enviar'}
                    </p>
                    {programado && (
                      <p className={classNames('text-sm mt-0.5 flex flex-wrap items-center gap-x-2', programado.estado === 'fallido' ? 'text-red-600' : 'text-blue-700')}>
                        <span>{textoProgramado(programado)}{programado.estado === 'pendiente' ? ` · a ${programado.para}` : ''}</span>
                        {programado.estado !== 'enviando' && (
                          <button onClick={() => cancelarCorreoProgramado(selectedLiq)} disabled={cancelandoProgramado} className="text-xs font-semibold underline">
                            {cancelandoProgramado ? 'Cancelando…' : programado.estado === 'fallido' ? 'Descartar' : 'Cancelar envío'}
                          </button>
                        )}
                      </p>
                    )}
                    {envio && (() => {
                      const st = estadoCorreo(envio);
                      return (
                        <p className={classNames('text-sm mt-0.5', st.tipo === 'no_entregado' ? 'text-red-600' : st.tipo === 'abierto' ? 'text-green-700' : 'text-sereno-dark/60')}>
                          {st.texto}
                        </p>
                      );
                    })()}
                  </div>
                </div>
                <button onClick={() => abrirPasosGuardada(selectedLiq, 6)} className={classNames(envio ? 'btn-ghost text-sm' : 'btn-primary', 'flex items-center gap-2 shrink-0')}>
                  <Send size={16} /> {programado?.estado === 'pendiente' ? 'Cambiar o enviar ya' : envio ? 'Enviar otra vez' : 'Enviar por email'}
                </button>
              </div>
            </div>
          );
        })()}

        {/* Payment action */}
        {(() => {
          const st = selectedLiq.paymentStatus || 'none';
          const isPaid = st === 'completed';
          const isProcessing = st === 'processing' || st === 'pending';
          const paidDate = selectedLiq.paymentDate
            ? new Date(selectedLiq.paymentDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })
            : null;

          if (isPaid) {
            const aMano = selectedLiq.paymentMethod === 'manual' && !selectedLiq.paymentQontoId;
            return (
              <div className="bg-green-50 border border-green-200 rounded-xl p-5 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="text-green-500 shrink-0" size={28} />
                  <div>
                    <p className="font-semibold text-green-800">{aMano ? 'Pagado (registrado a mano)' : 'Pagado con Qonto'}</p>
                    <p className="text-sm text-green-700/70 mt-0.5">
                      {aMano
                        ? `${paidDate ? `Pagado el ${paidDate}` : 'Pago registrado'}${selectedLiq.paymentManual?.markedBy ? ` · marcado por ${selectedLiq.paymentManual.markedBy}` : ''}`
                        : (paidDate ? `Transferencia completada el ${paidDate}` : 'Transferencia completada')}
                      {selectedLiq.paymentConcept ? ` · ${selectedLiq.paymentConcept}` : ''}
                    </p>
                  </div>
                </div>
                {aMano && (
                  <button
                    onClick={() => doUnmarkPaid(selectedLiq)}
                    disabled={unmarkingPaid}
                    className="btn-ghost text-sm flex items-center gap-2 shrink-0 disabled:opacity-50"
                  >
                    {unmarkingPaid ? <Loader2 className="animate-spin" size={14} /> : <Undo2 size={14} />}
                    Desmarcar
                  </button>
                )}
              </div>
            );
          }

          if (isProcessing) {
            return (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {syncingPayment
                    ? <Loader2 className="animate-spin text-amber-500 shrink-0" size={28} />
                    : <Clock className="text-amber-500 shrink-0" size={28} />}
                  <div>
                    <p className="font-semibold text-amber-800">Transferencia en proceso</p>
                    <p className="text-sm text-amber-700/70 mt-0.5">
                      {paidDate ? `Enviada el ${paidDate}. ` : ''}Qonto la está procesando.
                      {selectedLiq.paymentConcept ? ` · ${selectedLiq.paymentConcept}` : ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => openLiqDetail(selectedLiq)}
                  disabled={syncingPayment}
                  className="btn-ghost text-sm flex items-center gap-2 shrink-0"
                >
                  {syncingPayment ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                  Actualizar estado
                </button>
              </div>
            );
          }

          // not paid yet (none / failed) → show pay button
          return (
            <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-sereno-dark">Pago con Qonto</p>
                <p className="text-sm text-sereno-dark/50 mt-0.5">
                  {st === 'failed' ? 'El pago anterior falló. Puedes reintentar.' : 'Pendiente de pago'}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {st === 'none' && (
                  <button
                    onClick={() => abrirMarcarPagada([selectedLiq])}
                    className="btn-ghost text-sm flex items-center gap-2"
                    title="Registrar un pago hecho fuera de Qonto"
                  >
                    <CheckCircle2 size={16} /> Marcar como pagada
                  </button>
                )}
                <button
                  onClick={() => {
                    setSavedLiq(selectedLiq);
                    setPaymentConcept(`Liquidación ${selectedLiq.periodo} - ${selectedLiq.ownerName}`);
                    setPaymentDone(null);
                    setView('payment');
                  }}
                  className="btn-primary flex items-center gap-2"
                >
                  <CreditCard size={16} /> Pagar con Qonto
                </button>
              </div>
            </div>
          );
        })()}

        {markPaidModal}

        {cambiandoNumero && (
          <CambiarNumeroFactura
            liquidacion={selectedLiq}
            owner={owners.find(o => o.id === selectedLiq.ownerId)}
            esLaUltima={esUltimaDelPropietario(selectedLiq)}
            onGuardado={res => { aplicarNumeroCambiado(res); setCambiandoNumero(false); }}
            onCerrar={() => setCambiandoNumero(false)}
          />
        )}
      </div>
    );
  }
  if (view === 'new') {
    const pasoHecho = (num: WizardStep): boolean => {
      if (num === 5) {
        if (savedLiq?.result?.tipo === 'factura') {
          // Desde octubre de 2026, además, registrada en Holded como compra
          return savedLiq.invoiceNum != null && (!aplicaCompraHolded(savedLiq) || Boolean(savedLiq.holdedPurchase));
        }
        const aplica = (savedLiq?.modelo || 'gestion') === 'gestion' && savedLiq?.result?.tipo === 'liquidacion';
        return !aplica || Boolean(savedLiq?.holdedInvoice?.adjunto);
      }
      if (num === 6) return (savedLiq?.emailsEnviados || []).some((e: any) => e.estado === 'enviado') || savedLiq?.correoProgramado?.estado === 'pendiente';
      if (num === 7) return savedLiq?.paymentStatus === 'completed';
      return true;
    };
    const WIZARD_STEPS = [
      { label: 'Propietario', sub: 'y periodo' },
      { label: 'Reservas', sub: 'de Hostify' },
      { label: 'Gastos', sub: 'adicionales' },
      { label: 'Resumen', sub: 'y guardar' },
      { label: 'Factura', sub: (savedLiq?.result?.tipo ?? preview?.result?.tipo) === 'factura' ? 'del propietario' : 'en Holded' },
      { label: 'Email', sub: 'al propietario' },
      { label: 'Pago', sub: 'con Qonto' },
    ];

    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          {pasosDesdeFicha ? (
            <button onClick={volverAFicha} className="text-sm text-sereno-dark/50 hover:text-sereno-dark mb-1">
              ← Volver a la liquidación
            </button>
          ) : (
            <button onClick={() => { setView('list'); resetForm(); }} className="text-sm text-sereno-dark/50 hover:text-sereno-dark mb-1">
              ← Volver al listado
            </button>
          )}
          <h1 className="text-2xl font-bold text-sereno-dark">
            {pasosDesdeFicha && savedLiq ? `Liquidación ${savedLiq.periodo} · ${savedLiq.ownerName}` : 'Nueva Liquidación'}
          </h1>
        </div>

        {/* Stepper */}
        <div className="bg-white border border-gray-100 rounded-xl px-6 py-5 shadow-sm">
          <div className="flex items-start">
            {WIZARD_STEPS.map((step, i) => {
              const num = (i + 1) as WizardStep;
              // Del 5 al 7, con la liquidación ya guardada, el ✓ dice si eso se
              // hizo de verdad, no solo si el paso quedó atrás: se pueden saltar
              const completed = savedLiq && num >= 5 ? pasoHecho(num) : wizardStep > num;
              const active = wizardStep === num;
              // Guardada la liquidación, los pasos 1-4 quedan cerrados (volver a
              // ellos la guardaría otra vez) y del 5 al 7 se va y se viene libremente
              const clickable = savedLiq ? num >= 5 && !active : completed;
              return (
                <div key={i} className="contents">
                  <button
                    className="flex flex-col items-center gap-1.5 min-w-0"
                    onClick={() => { if (clickable) setWizardStep(num); }}
                    style={{ cursor: clickable ? 'pointer' : 'default' }}
                  >
                    <div className={classNames(
                      'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all shrink-0',
                      completed ? 'bg-green-500 text-white' : active ? 'bg-sereno-dark text-white ring-4 ring-sereno-dark/10' : 'bg-gray-100 text-gray-400'
                    )}>
                      {completed ? '✓' : num}
                    </div>
                    <span className={classNames('text-xs font-semibold text-center hidden sm:block whitespace-nowrap', active ? 'text-sereno-dark' : completed ? 'text-green-600' : 'text-gray-400')}>
                      {step.label}
                    </span>
                    <span className={classNames('text-xs text-center hidden sm:block whitespace-nowrap', active ? 'text-sereno-dark/50' : 'text-gray-300')}>
                      {step.sub}
                    </span>
                  </button>
                  {i < WIZARD_STEPS.length - 1 && (
                    <div className={classNames('flex-1 h-0.5 mt-4 mx-3', completed ? 'bg-green-400' : 'bg-gray-200')} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step 1: Propietario y periodo */}
        {wizardStep === 1 && (
          <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-sereno-dark">Propietario y periodo</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Propietario</label>
                <select className="input" value={selectedOwnerId} onChange={e => { setSelectedOwnerId(e.target.value); setReservas([]); setCancelledReservas([]); setPreview(null); }}>
                  <option value="">Seleccionar...</option>
                  {owners
                    .filter(o => o.status !== 'inactive')
                    .sort((a: any, b: any) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
                    .map(o => (
                      <option key={o.id} value={o.id}>
                        {o.firstName} {o.lastName}{o.contractModel ? ` (${MODEL_LABELS[o.contractModel] || o.contractModel})` : ''}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Mes</label>
                <select className="input" value={month} onChange={e => { setMonth(Number(e.target.value)); setReservas([]); setCancelledReservas([]); setPreview(null); }}>
                  {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Año</label>
                <select className="input" value={year} onChange={e => { setYear(Number(e.target.value)); setReservas([]); setCancelledReservas([]); setPreview(null); }}>
                  {[2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            {selectedOwner && !selectedOwner.contractModel && (
              <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                ⚠️ Este propietario no tiene modelo de contrato configurado. Edítalo antes de generar la liquidación.
              </div>
            )}
            {selectedOwner && selectedOwner.contractModel && (
              <div className="flex items-center gap-3 text-sm text-sereno-dark/70 bg-gray-50 rounded-lg p-3">
                <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border', MODEL_COLORS[selectedOwner.contractModel] || '')}>
                  {MODEL_LABELS[selectedOwner.contractModel] || selectedOwner.contractModel}
                </span>
                <span>Comisión: {selectedOwner.commissionPercent ?? 20}%</span>
                {selectedOwner.propertyNames?.length > 0 && (
                  <span>· {selectedOwner.propertyNames.length} propiedad(es)</span>
                )}
              </div>
            )}
            <div className="flex justify-end pt-2">
              <button
                onClick={fetchReservations}
                disabled={!selectedOwnerId || loadingReservas}
                className="btn-primary flex items-center gap-2"
              >
                {loadingReservas ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
                Obtener reservas de Hostify
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Reservas */}
        {wizardStep === 2 && (() => {
          const isExplotacion = ['explotacion', 'explotacion_lcm', 'explotacion_sin_irpf'].includes(selectedOwner?.contractModel || '');
          const isLcm = selectedOwner?.contractModel === 'explotacion_lcm';
          // LCM: el neto por fila viene del server (base imponible); fallback al cálculo antiguo para datos ya guardados
          const rowIngresos = (r: any) => {
            const canalSinIva = r.canalSinIva ?? ((r.comisionCanal || 0) + (r.tarifaTransaccion || 0));
            const legacy = (r.precioTotal || 0) - (r.limpieza || 0) - canalSinIva - (r.comisionSereno || 0) - (r.renting || 0);
            return isLcm ? (r.netoLinea ?? legacy) : legacy;
          };
          const rowLimpieza = (r: any) => (isLcm ? (r.limpiezaBI ?? r.limpieza) : r.limpieza);
          return (
          <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-sereno-dark">Reservas encontradas</h2>
              <div className="flex items-center gap-3">
                {reservas.length > 0 && (
                  <button
                    onClick={() => {
                      if (editReservas) { recomputeReservas(); }
                      setEditReservas(v => !v);
                    }}
                    disabled={recomputingReservas}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors flex items-center gap-1.5 ${editReservas ? 'bg-sereno-dark text-white border-sereno-dark' : 'border-gray-200 text-sereno-dark/70 hover:bg-gray-50'}`}
                  >
                    {recomputingReservas ? <Loader2 className="animate-spin" size={13} /> : editReservas ? <CheckCircle size={13} /> : <Settings size={13} />}
                    {editReservas ? 'Hecho' : 'Editar valores'}
                  </button>
                )}
                <span className="text-sm text-sereno-dark/50">{selectedOwner?.firstName} {selectedOwner?.lastName} · {MONTHS[month]} {year}</span>
              </div>
            </div>
            {reservas.length > 0 && editReservas && (
              <p className="text-xs text-amber-600 -mt-2">Modo edición: cambia los valores capturados de Hostify. Al pulsar «Hecho» se recalculan Sereno, Renting e Ingresos.</p>
            )}
            {reservas.length > 0 && (
              <p className="text-xs text-sereno-dark/40 -mt-2">{isLcm ? 'Importes en base imponible: Limpieza sin su 21% de IVA, Com. canal y Renting sin IVA. Ingresos por fila = Total/1,10 − com. canal − limpieza BI − renting (estancias del propietario no generan base).' : isExplotacion ? 'Com. canal, Sereno y Renting se muestran sin IVA.' : 'Com. canal, Sereno y Renting se muestran con IVA (21%) incluido.'}</p>
            )}
            {reservas.length === 0 ? (
              <div className="py-12 text-center text-sereno-dark/40">
                <p className="font-medium">No hay reservas en este período</p>
                <p className="text-sm mt-1">Puedes continuar igualmente o volver a cambiar el período.</p>
              </div>
            ) : isExplotacion ? (
              <>
                {/* Desktop table — Modelo de explotación (sin IVA) */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-sereno-dark text-white">
                        <th className="text-left py-2 px-3 rounded-tl-lg">Propiedad</th>
                        <th className="text-left py-2 px-3">Huésped</th>
                        <th className="text-left py-2 px-3">Entrada</th>
                        <th className="text-right py-2 px-3">Huésp.</th>
                        <th className="text-left py-2 px-3">Canal</th>
                        <th className="text-right py-2 px-3">Total</th>
                        <th className="text-right py-2 px-3">Limpieza</th>
                        <th className="text-right py-2 px-3">Com. canal</th>
                        <th className="text-right py-2 px-3">Sereno</th>
                        <th className="text-right py-2 px-3">Renting</th>
                        <th className="text-right py-2 px-3 rounded-tr-lg">Ingresos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservas.map((r: any, i: number) => {
                        const canalSinIva = r.canalSinIva ?? ((r.comisionCanal || 0) + (r.tarifaTransaccion || 0));
                        const rentingSinIva = r.renting || 0;
                        const serenoSinIva = r.comisionSereno || 0;
                        const ingresosSinIva = rowIngresos(r);
                        return (
                          <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                            <td className="py-2 px-3 font-medium">{r.anuncio}</td>
                            <td className="py-2 px-3 capitalize">
                              {editReservas ? (
                                <div className="flex flex-col gap-1">
                                  <input
                                    type="text"
                                    value={r.huesped ?? ''}
                                    onChange={e => updateReservaField(i, 'huesped', e.target.value)}
                                    className={`w-32 text-left ${editInputCls}`}
                                  />
                                  <label className="flex items-center gap-1 text-[10px] text-sereno-dark/60 normal-case cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={!!r.isExtension}
                                      onChange={e => updateReservaField(i, 'isExtension', e.target.checked)}
                                      className="rounded"
                                    />
                                    ampliación
                                  </label>
                                </div>
                              ) : (
                                <>
                                  {r.huesped}
                                  {r.isExtension && (
                                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">ampliación</span>
                                  )}
                                  {r.isCancelled && (
                                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">cancelada</span>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="py-2 px-3">{r.entrada}</td>
                            <td className="py-2 px-3 text-right text-sereno-dark/50">{numCell(i, 'huespedes', r.huespedes, r.huespedes ?? '—', 'w-14')}</td>
                            <td className="py-2 px-3">
                              {editReservas ? (
                                <input
                                  type="text"
                                  value={r.canal ?? ''}
                                  onChange={e => updateReservaField(i, 'canal', e.target.value)}
                                  className={`w-28 text-left ${editInputCls}`}
                                />
                              ) : r.canal}
                            </td>
                            <td className="py-2 px-3 text-right font-medium">{numCell(i, 'precioTotal', r.precioTotal, fmt(r.precioTotal))}</td>
                            <td className="py-2 px-3 text-right">{numCell(i, 'limpieza', r.limpieza, fmt(rowLimpieza(r)))}</td>
                            <td className="py-2 px-3 text-right">{fmt(canalSinIva)}</td>
                            <td className="py-2 px-3 text-right">{isLcm ? '—' : fmt(serenoSinIva)}</td>
                            <td className="py-2 px-3 text-right">{rentingCell(i, r, rentingSinIva > 0 ? fmt(rentingSinIva) : '—')}</td>
                            <td className="py-2 px-3 text-right font-semibold text-green-700">{fmt(ingresosSinIva)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-semibold">
                        <td className="py-2 px-3" colSpan={5}>Totales</td>
                        <td className="py-2 px-3 text-right">{fmt(reservas.reduce((s: number, r: any) => s + r.precioTotal, 0))}</td>
                        <td className="py-2 px-3 text-right">{fmt(reservas.reduce((s: number, r: any) => s + (rowLimpieza(r) || 0), 0))}</td>
                        <td className="py-2 px-3 text-right">{fmt(reservas.reduce((s: number, r: any) => s + (r.canalSinIva ?? (r.comisionCanal || 0) + (r.tarifaTransaccion || 0)), 0))}</td>
                        <td className="py-2 px-3 text-right">{isLcm ? '—' : fmt(reservas.reduce((s: number, r: any) => s + (r.comisionSereno || 0), 0))}</td>
                        <td className="py-2 px-3 text-right">{fmt(reservas.reduce((s: number, r: any) => s + (r.renting || 0), 0))}</td>
                        <td className="py-2 px-3 text-right text-green-700">{fmt(reservas.reduce((s: number, r: any) => s + rowIngresos(r), 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {/* Mobile cards — Modelo de explotación (sin IVA) */}
                <div className="sm:hidden space-y-2">
                  {reservas.map((r: any, i: number) => {
                    const canalSinIva = r.canalSinIva ?? ((r.comisionCanal || 0) + (r.tarifaTransaccion || 0));
                    const rentingSinIva = r.renting || 0;
                    const serenoSinIva = r.comisionSereno || 0;
                    const ingresosSinIva = rowIngresos(r);
                    return (
                      <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-sereno-dark text-sm">{r.anuncio}</p>
                          <p className="text-green-700 font-semibold text-sm shrink-0">{fmt(r.precioTotal)}</p>
                        </div>
                        <p className="text-xs text-sereno-dark/70 capitalize">
                          {r.huesped}
                          {r.isExtension && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">ampliación</span>
                          )}
                          {r.isCancelled && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">cancelada</span>
                          )}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-sereno-dark/60">
                          <span>{r.entrada}</span>
                          <span className="capitalize">{r.canal}</span>
                          {r.huespedes != null && <span>{r.huespedes} pers.</span>}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-sereno-dark/60 pt-0.5">
                          <span>Limpieza: {fmt(rowLimpieza(r))}</span>
                          <span>Com. canal: {fmt(canalSinIva)}</span>
                          {rentingSinIva > 0 && <span>Renting: {fmt(rentingSinIva)}</span>}
                          {!isLcm && <span>Sereno: {fmt(serenoSinIva)}</span>}
                        </div>
                        <div className="text-xs font-semibold text-green-700 pt-0.5">Ingresos: {fmt(ingresosSinIva)}</div>
                      </div>
                    );
                  })}
                  <div className="bg-gray-100 rounded-lg p-3 flex items-center justify-between text-sm font-semibold">
                    <span>Total ingresos</span>
                    <span>{fmt(reservas.reduce((s: number, r: any) => s + rowIngresos(r), 0))}</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Desktop table — Otros modelos */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-sereno-dark text-white">
                        <th className="text-left py-2 px-3 rounded-tl-lg">Propiedad</th>
                        <th className="text-left py-2 px-3">Huésped</th>
                        <th className="text-left py-2 px-3">Entrada</th>
                        <th className="text-right py-2 px-3">Huésp.</th>
                        <th className="text-left py-2 px-3">Canal</th>
                        <th className="text-right py-2 px-3">Total</th>
                        <th className="text-right py-2 px-3">Limpieza</th>
                        <th className="text-right py-2 px-3">Com. canal</th>
                        <th className="text-right py-2 px-3">Sereno</th>
                        <th className="text-right py-2 px-3">Renting</th>
                        <th className="text-right py-2 px-3 rounded-tr-lg">Ingresos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservas.map((r: any, i: number) => (
                        <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                          <td className="py-2 px-3 font-medium">{r.anuncio}</td>
                          <td className="py-2 px-3 capitalize">
                            {editReservas ? (
                              <div className="flex flex-col gap-1">
                                <input
                                  type="text"
                                  value={r.huesped ?? ''}
                                  onChange={e => updateReservaField(i, 'huesped', e.target.value)}
                                  className={`w-32 text-left ${editInputCls}`}
                                />
                                <label className="flex items-center gap-1 text-[10px] text-sereno-dark/60 normal-case cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={!!r.isExtension}
                                    onChange={e => updateReservaField(i, 'isExtension', e.target.checked)}
                                    className="rounded"
                                  />
                                  ampliación
                                </label>
                              </div>
                            ) : (
                              <>
                                {r.huesped}
                                {r.isExtension && (
                                  <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">ampliación</span>
                                )}
                                {r.isCancelled && (
                                  <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">cancelada</span>
                                )}
                              </>
                            )}
                          </td>
                          <td className="py-2 px-3">{r.entrada}</td>
                          <td className="py-2 px-3 text-right text-sereno-dark/50">{numCell(i, 'huespedes', r.huespedes, r.huespedes ?? '—', 'w-14')}</td>
                          <td className="py-2 px-3">
                            {editReservas ? (
                              <input
                                type="text"
                                value={r.canal ?? ''}
                                onChange={e => updateReservaField(i, 'canal', e.target.value)}
                                className={`w-28 text-left ${editInputCls}`}
                              />
                            ) : r.canal}
                          </td>
                          <td className="py-2 px-3 text-right font-medium">{numCell(i, 'precioTotal', r.precioTotal, fmt(r.precioTotal))}</td>
                          <td className="py-2 px-3 text-right">{numCell(i, 'limpieza', r.limpieza, fmt(r.limpieza))}</td>
                          <td className="py-2 px-3 text-right">
                            {editReservas ? (
                              <input
                                type="number"
                                step="0.01"
                                value={(r.comisionCanal || 0) + (r.tarifaTransaccion || 0)}
                                onChange={e => {
                                  const v = e.target.value === '' ? 0 : Number(e.target.value);
                                  setReservas(prev => prev.map((x, idx) => idx === i ? { ...x, comisionCanal: v, tarifaTransaccion: 0 } : x));
                                }}
                                className={`w-24 ${editInputCls}`}
                              />
                            ) : fmt(r.canalConIva ?? (r.comisionCanal || 0) + (r.tarifaTransaccion || 0))}
                          </td>
                          <td className="py-2 px-3 text-right">{r.comisionSereno != null ? fmt(r.comisionSereno + (r.ivaSereno || 0)) : '—'}</td>
                          <td className="py-2 px-3 text-right">{rentingCell(i, r, r.rentingConIva != null && r.rentingConIva > 0 ? fmt(r.rentingConIva) : '—', true)}</td>
                          <td className="py-2 px-3 text-right font-semibold text-green-700">{r.ingresos != null ? fmt(r.ingresos) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-semibold">
                        <td className="py-2 px-3" colSpan={5}>Totales</td>
                        <td className="py-2 px-3 text-right">{fmt(reservas.reduce((s: number, r: any) => s + r.precioTotal, 0))}</td>
                        <td className="py-2 px-3 text-right">{fmt(reservas.reduce((s: number, r: any) => s + r.limpieza, 0))}</td>
                        <td className="py-2 px-3 text-right">{fmt(reservas.reduce((s: number, r: any) => s + (r.canalConIva ?? (r.comisionCanal || 0) + (r.tarifaTransaccion || 0)), 0))}</td>
                        <td className="py-2 px-3 text-right">{fmt(reservas.reduce((s: number, r: any) => s + (r.comisionSereno || 0) + (r.ivaSereno || 0), 0))}</td>
                        <td className="py-2 px-3 text-right">{fmt(reservas.reduce((s: number, r: any) => s + (r.rentingConIva || 0), 0))}</td>
                        <td className="py-2 px-3 text-right text-green-700">{fmt(reservas.reduce((s: number, r: any) => s + (r.ingresos || 0), 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {/* Mobile cards — Otros modelos */}
                <div className="sm:hidden space-y-2">
                  {reservas.map((r: any, i: number) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sereno-dark text-sm">{r.anuncio}</p>
                        <p className="text-green-700 font-semibold text-sm shrink-0">{fmt(r.precioTotal)}</p>
                      </div>
                      <p className="text-xs text-sereno-dark/70 capitalize">
                        {r.huesped}
                        {r.isExtension && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">ampliación</span>
                        )}
                        {r.isCancelled && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">cancelada</span>
                        )}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-sereno-dark/60">
                        <span>{r.entrada}</span>
                        <span className="capitalize">{r.canal}</span>
                        {r.huespedes != null && <span>{r.huespedes} pers.</span>}
                      </div>
                      <div className="flex gap-4 text-xs text-sereno-dark/60 pt-0.5">
                        <span>Limpieza: {fmt(r.limpieza)}</span>
                        <span>Com. canal: {fmt(r.canalConIva ?? (r.comisionCanal || 0) + (r.tarifaTransaccion || 0))}</span>
                        {r.rentingConIva != null && r.rentingConIva > 0 && <span>Renting: {fmt(r.rentingConIva)}</span>}
                        {r.comisionSereno != null && <span>Sereno: {fmt(r.comisionSereno + (r.ivaSereno || 0))}</span>}
                      </div>
                      {r.ingresos != null && (
                        <div className="text-xs font-semibold text-green-700 pt-0.5">Ingresos: {fmt(r.ingresos)}</div>
                      )}
                    </div>
                  ))}
                  <div className="bg-gray-100 rounded-lg p-3 flex items-center justify-between text-sm font-semibold">
                    <span>Total</span>
                    <span>{fmt(reservas.reduce((s: number, r: any) => s + r.precioTotal, 0))}</span>
                  </div>
                </div>
              </>
            )}
            {/* Reservas canceladas con penalización cobrada — siempre visibles */}
            {cancelledConPenalizacion.length > 0 && (
              <div className="border-2 border-amber-300 bg-amber-50 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-amber-100 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                      <AlertTriangle size={15} className="text-amber-600" />
                      Canceladas con penalización cobrada ({cancelledConPenalizacion.length})
                    </h3>
                    <p className="text-xs text-amber-800 mt-1">
                      El huésped canceló fuera del período de gracia y perdió este dinero, así que corresponde al propietario.
                      Revísalas y añádelas a la liquidación de arriba si procede. El Total propuesto es la penalización que reporta Hostify, con limpieza y renting a 0 — todo editable con «Editar valores».
                    </p>
                  </div>
                  {cancelledAñadidas > 0 && (
                    <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
                      {cancelledAñadidas} añadida{cancelledAñadidas > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="p-4 pt-3">{renderCancelledList(cancelledConPenalizacion, true)}</div>
              </div>
            )}

            {/* Resto de canceladas (sin penalización) — desplegable */}
            {cancelledSinPenalizacion.length > 0 && (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowCancelled(v => !v)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-sereno-dark">
                    <Ban size={15} className="text-sereno-dark/40" />
                    Otras canceladas sin penalización ({cancelledSinPenalizacion.length})
                    <span className="text-xs font-normal text-sereno-dark/40">Hostify no reporta importe retenido</span>
                  </span>
                  {showCancelled ? <ChevronUp size={16} className="text-sereno-dark/40 shrink-0" /> : <ChevronDown size={16} className="text-sereno-dark/40 shrink-0" />}
                </button>
                {showCancelled && (
                  <div className="p-4 space-y-3 border-t border-gray-200">
                    <p className="text-xs text-sereno-dark/50">
                      Estas cancelaciones no generaron cargo al huésped. Si sabes que sí hubo penalización, puedes añadirlas igualmente: entrarán con el total de la reserva y podrás ajustar el importe con «Editar valores».
                    </p>
                    {renderCancelledList(cancelledSinPenalizacion, false)}
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-between pt-2">
              <button onClick={() => setWizardStep(1)} className="btn-ghost flex items-center gap-2">← Atrás</button>
              <button onClick={() => setWizardStep(3)} className="btn-primary flex items-center gap-2">Continuar →</button>
            </div>
          </div>
          );
        })()}

        {/* Step 3: Gastos */}
        {wizardStep === 3 && (
          <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-sereno-dark">Gastos adicionales</h2>
              <button onClick={addExpense} className="btn-ghost flex items-center gap-1 text-sm">
                <Plus size={14} /> Añadir gasto
              </button>
            </div>
            {expenses.length === 0 && (
              <p className="text-sm text-sereno-dark/40 py-4 text-center">No hay gastos adicionales. Puedes añadirlos manualmente o continuar sin ellos.</p>
            )}
            {expenses.length > 0 && (() => {
              const preloaded = expenses.map((e, i) => ({ e, i, parsed: parseExpenseConcept(e.concept) })).filter(x => x.parsed.tag !== null);
              const manual = expenses.map((e, i) => ({ e, i })).filter(({ e }) => parseExpenseConcept(e.concept).tag === null);
              const tagGroups = new Map<string, typeof preloaded>();
              for (const item of preloaded) {
                const key = item.parsed.tag!;
                if (!tagGroups.has(key)) tagGroups.set(key, []);
                tagGroups.get(key)!.push(item);
              }
              return (
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  {Array.from(tagGroups.entries()).map(([tag, items]) => (
                    <div key={tag} className="border-b border-gray-200 last:border-0">
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-sereno-beau/30 text-sereno-dark border border-sereno-beau/50">{tag}</span>
                      </div>
                      {items.map(({ e, i, parsed }) => (
                        <div key={i} className={`flex items-center justify-between px-4 py-2.5 border-b border-gray-100 last:border-0 ${e.excluded ? 'bg-gray-50 opacity-50' : 'bg-white'}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <input type="checkbox" checked={!e.excluded} onChange={() => toggleExpenseExcluded(i)} className="shrink-0 w-4 h-4 accent-sereno-dark cursor-pointer" />
                            <span className={`text-sm truncate ${e.excluded ? 'line-through text-sereno-dark/40' : 'text-sereno-dark'}`}>{parsed.name}</span>
                            {parsed.property && (
                              <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs text-gray-400 bg-gray-100 border border-gray-200">{parsed.property}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 ml-4 shrink-0">
                            <input type="number" className="input w-28 text-sm text-right" placeholder="0.00" step="0.01" value={e.amount} onChange={ev => updateExpense(i, 'amount', ev.target.value)} disabled={e.excluded} />
                            <button onClick={() => removeExpense(i)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"><Trash2 size={15} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                  {manual.map(({ e, i }) => (
                    <div key={i} className={`flex flex-col sm:grid sm:grid-cols-[auto_1fr_auto_auto] sm:items-center px-4 py-2.5 gap-2 sm:gap-3 border-b border-gray-100 last:border-0 ${e.excluded ? 'bg-gray-50 opacity-50' : 'bg-white'}`}>
                      <input type="checkbox" checked={!e.excluded} onChange={() => toggleExpenseExcluded(i)} className="shrink-0 w-4 h-4 accent-sereno-dark cursor-pointer" />
                      <input className={`input text-sm ${e.excluded ? 'line-through text-sereno-dark/40' : ''}`} placeholder="Concepto del gasto" value={e.concept} onChange={ev => updateExpense(i, 'concept', ev.target.value)} disabled={e.excluded} />
                      <div className="flex items-center gap-2">
                        <input type="number" className="input flex-1 sm:w-28 text-sm text-right" placeholder="0.00" step="0.01" value={e.amount} onChange={ev => updateExpense(i, 'amount', ev.target.value)} disabled={e.excluded} />
                        <button onClick={() => removeExpense(i)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="flex justify-between pt-2">
              <button onClick={() => setWizardStep(2)} className="btn-ghost flex items-center gap-2">← Atrás</button>
              <button onClick={doPreview} disabled={loadingPreview} className="btn-primary flex items-center gap-2">
                {loadingPreview ? <Loader2 className="animate-spin" size={16} /> : <Calculator size={16} />}
                Calcular liquidación
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Resumen & guardar */}
        {wizardStep === 4 && preview && (
          <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-sereno-dark">Resumen</h2>
            <ResumenConceptos conceptos={preview.result.conceptos || []} saldo={preview.result.saldo} tipo={preview.result.tipo} />
            <div className="flex justify-between pt-4">
              <button onClick={() => setWizardStep(3)} className="btn-ghost flex items-center gap-2">← Atrás</button>
              <button onClick={handleSaveClick} disabled={saving} className="btn-primary flex items-center gap-2">
                {saving ? <Loader2 className="animate-spin" size={16} /> : <Receipt size={16} />}
                Guardar liquidación
              </button>
            </div>
          </div>
        )}

        {/* Mark expenses as COBRADO dialog */}
        {showMarkCobradoDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full space-y-4">
              <h3 className="text-lg font-bold text-sereno-dark">Gastos de lavandería</h3>
              <p className="text-sm text-sereno-dark/70">
                Hay <strong>{expenses.filter(e => e.docId).length}</strong> gasto(s) de lavandería/mantenimiento pendientes incluidos en esta liquidación. ¿Marcarlos como <strong>COBRADO</strong> en la base de datos?
              </p>
              <div className="flex gap-3 pt-2">
                <button onClick={() => performSave(false)} className="btn-ghost flex-1 text-sm">No, solo guardar</button>
                <button onClick={() => performSave(true)} className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm">
                  <CheckCircle2 size={15} /> Sí, marcar cobrados
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Factura en Holded */}
        {wizardStep === 5 && savedLiq && (savedLiq.result?.tipo === 'factura' ? (
          <PasoFacturaPropietario
            liquidacion={savedLiq}
            owner={owners.find(o => o.id === savedLiq.ownerId)}
            esLaUltima={esUltimaDelPropietario(savedLiq)}
            generarPdf={pdfDocBlob}
            generarPdfBase64={pdfLiquidacionBase64}
            onNumeroCambiado={aplicarNumeroCambiado}
            onLiquidacionActualizada={actualizarLiqGuardada}
            onContinuar={() => setWizardStep(6)}
          />
        ) : (
          <PasoFacturaHolded
            liquidacion={savedLiq}
            generarPdfLiquidacion={() => pdfLiquidacionBase64(savedLiq)}
            onLiquidacionActualizada={actualizarLiqGuardada}
            onContinuar={() => setWizardStep(6)}
          />
        ))}

        {/* Step 6: Correo al propietario */}
        {wizardStep === 6 && savedLiq && (
          <PasoEmailPropietario
            liquidacion={savedLiq}
            owner={selectedOwner || owners.find(o => o.id === savedLiq.ownerId)}
            generarPdfLiquidacion={() => pdfLiquidacionBase64(savedLiq)}
            onLiquidacionActualizada={actualizarLiqGuardada}
            onAtras={() => setWizardStep(5)}
            onContinuar={() => setWizardStep(7)}
          />
        )}

        {/* Step 7: Pago con Qonto */}
        {wizardStep === 7 && savedLiq && (() => {
          const ibanOwner = selectedOwner?.iban || owners.find(o => o.id === savedLiq.ownerId)?.iban;
          const canPay = !!ibanOwner && !paymentDone?.success;
          const liqStatus = savedLiq.paymentStatus || 'none';
          const alreadyPaid = !paymentDone && liqStatus === 'completed';
          const alreadyProcessing = !paymentDone && (liqStatus === 'processing' || liqStatus === 'pending') && !!savedLiq.paymentQontoId;
          const paidDate = savedLiq.paymentDate
            ? new Date(savedLiq.paymentDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })
            : null;
          return (
            <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-sereno-dark">Pago con Qonto</h2>
                <span className={classNames('text-2xl font-bold', saldoAFavorDeSereno(savedLiq.result?.saldo) ? 'text-amber-700' : 'text-sereno-dark')}>{fmt(savedLiq.result?.saldo || 0)}</span>
              </div>
              {alreadyPaid ? (
                <div className="flex flex-col items-center gap-3 py-6">
                  <CheckCircle2 className="text-green-500" size={48} />
                  <p className="text-lg font-semibold text-sereno-dark">Liquidación pagada</p>
                  <p className="text-sm text-sereno-dark/60">
                    {paidDate ? `Transferencia completada el ${paidDate}.` : 'Transferencia completada.'}
                  </p>
                  <button onClick={salirDelAsistente} className="btn-primary mt-2">{pasosDesdeFicha ? 'Volver a la liquidación' : 'Volver al listado'}</button>
                </div>
              ) : alreadyProcessing ? (
                <div className="flex flex-col items-center gap-3 py-6">
                  {syncingPayment ? <Loader2 className="animate-spin text-amber-500" size={48} /> : <Clock className="text-amber-500" size={48} />}
                  <p className="text-lg font-semibold text-sereno-dark">Transferencia en proceso</p>
                  <p className="text-sm text-sereno-dark/60">
                    {paidDate ? `Enviada el ${paidDate}. ` : ''}Qonto la está procesando.
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <button
                      onClick={async () => {
                        setSyncingPayment(true);
                        try {
                          const result: any = await api.getLiquidacionPaymentStatus(savedLiq.id);
                          const updatedLiq = result?.liquidacion;
                          if (updatedLiq) {
                            setSavedLiq(updatedLiq);
                            setSelectedLiq((prev: any) => prev && prev.id === updatedLiq.id ? updatedLiq : prev);
                            setLiquidaciones(prev => prev.map(l => l.id === updatedLiq.id ? updatedLiq : l));
                          }
                        } catch { /* ignore */ } finally {
                          setSyncingPayment(false);
                        }
                      }}
                      disabled={syncingPayment}
                      className="btn-ghost text-sm flex items-center gap-2"
                    >
                      {syncingPayment ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                      Actualizar estado
                    </button>
                    <button onClick={salirDelAsistente} className="btn-primary">{pasosDesdeFicha ? 'Volver a la liquidación' : 'Volver al listado'}</button>
                  </div>
                </div>
              ) : paymentDone?.success ? (
                <div className="flex flex-col items-center gap-3 py-6">
                  <CheckCircle2 className="text-green-500" size={48} />
                  <p className="text-lg font-semibold text-sereno-dark">Transferencia enviada</p>
                  <p className="text-sm text-sereno-dark/60">El pago se está procesando. Puedes verificar el estado en Qonto.</p>
                  <button onClick={salirDelAsistente} className="btn-primary mt-2">{pasosDesdeFicha ? 'Volver a la liquidación' : 'Volver al listado'}</button>
                </div>
              ) : paymentDone && !paymentDone.success ? (
                <div className="flex flex-col items-center gap-3 py-6">
                  <AlertTriangle className="text-red-500" size={48} />
                  <p className="text-lg font-semibold text-sereno-dark">Error en el pago</p>
                  <p className="text-sm text-red-600">{paymentDone.error}</p>
                  <button onClick={() => setPaymentDone(null)} className="btn-primary mt-2">Reintentar</button>
                </div>
              ) : !(Number(savedLiq.result?.saldo) > 0) ? (
                <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>
                    {saldoAFavorDeSereno(savedLiq.result?.saldo)
                      ? <>El saldo es <strong>a favor de Sereno</strong>: el propietario debe {fmt(Math.abs(Number(savedLiq.result?.saldo)))}, que van en la factura de Sereno. No hay nada que transferirle.</>
                      : 'El saldo es cero: no hay nada que transferir al propietario.'}
                  </span>
                  <button onClick={salirDelAsistente} className="btn-ghost text-sm ml-auto whitespace-nowrap">{pasosDesdeFicha ? 'Volver a la liquidación' : 'Volver al listado'}</button>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">IBAN del propietario</label>
                      {ibanOwner ? (
                        <div className="input bg-gray-50 text-sereno-dark/80 font-mono text-sm">{ibanOwner}</div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <AlertTriangle size={16} /> Este propietario no tiene IBAN configurado. Edítalo en su ficha antes de pagar.
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Concepto de la transferencia</label>
                      <input className="input" value={paymentConcept} onChange={e => setPaymentConcept(e.target.value)} maxLength={140} placeholder="Concepto de la transferencia" />
                      <p className="text-xs text-sereno-dark/40 mt-1">{paymentConcept.length}/140 caracteres</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                    <button onClick={() => setShowPayConfirm(true)} disabled={!canPay || paying} className="btn-primary flex items-center justify-center gap-2">
                      {paying ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                      Pagar con Qonto
                    </button>
                    <button onClick={salirDelAsistente} className="btn-ghost text-sm">
                      {pasosDesdeFicha ? 'Salir sin pagar' : 'Guardar sin pagar'}
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* Pay confirm modal (wizard step 7) */}
        {wizardStep === 7 && showPayConfirm && savedLiq && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full space-y-4">
              <h3 className="text-lg font-bold text-sereno-dark">Confirmar transferencia</h3>
              <p className="text-sm text-sereno-dark/70">
                Se enviará <strong>{fmt(Math.abs(savedLiq.result?.saldo || 0))}</strong> a <strong>{savedLiq.ownerName}</strong>.<br />
                IBAN: <span className="font-mono text-xs">{selectedOwner?.iban || owners.find(o => o.id === savedLiq.ownerId)?.iban}</span><br />
                Concepto: {paymentConcept}
              </p>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowPayConfirm(false)} className="btn-ghost flex-1">Cancelar</button>
                <button onClick={doPayLiquidacion} className="btn-primary flex-1 flex items-center justify-center gap-2">
                  <Send size={16} /> Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- LIST VIEW ---
  const years = [...new Set(liquidaciones.map(l => l.year))].sort((a, b) => b - a);
  if (years.length === 0) years.push(new Date().getFullYear());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-sereno-dark">Liquidaciones</h1>
        {activeTab === 'liquidaciones' ? (
          <button onClick={() => setView('new')} className="btn-primary flex items-center gap-2 self-start sm:self-auto">
            <Plus size={16} /> Nueva liquidación
          </button>
        ) : activeTab === 'remesas' ? (
          <button onClick={() => setShowGenerateRemesa(true)} className="btn-primary flex items-center gap-2 self-start sm:self-auto">
            <Layers size={16} /> Generar remesa
          </button>
        ) : null}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('liquidaciones')}
          className={classNames('px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors', activeTab === 'liquidaciones' ? 'border-sereno-dark text-sereno-dark' : 'border-transparent text-sereno-dark/50 hover:text-sereno-dark')}
        >
          Liquidaciones
        </button>
        <button
          onClick={() => setActiveTab('remesas')}
          className={classNames('px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors', activeTab === 'remesas' ? 'border-sereno-dark text-sereno-dark' : 'border-transparent text-sereno-dark/50 hover:text-sereno-dark')}
        >
          Remesas
        </button>
        <button
          onClick={() => setActiveTab('pendingPricing')}
          className={classNames('px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5', activeTab === 'pendingPricing' ? 'border-amber-600 text-amber-700' : 'border-transparent text-sereno-dark/50 hover:text-sereno-dark')}
        >
          Gastos sin precio
          {pendingCount > 0 && (
            <span className="bg-amber-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-none">{pendingCount}</span>
          )}
        </button>
      </div>

      {/* ── LIQUIDACIONES TAB ── */}
      {activeTab === 'liquidaciones' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <select className="input w-auto" value={filterYear} onChange={e => { setFilterYear(Number(e.target.value)); setFilterMonth(0); }}>
              <option value={0}>Todos los años</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select className="input w-auto" value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))}>
              <option value={0}>Todos los meses</option>
              {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select className="input w-auto" value={filterOwner} onChange={e => setFilterOwner(e.target.value)}>
              <option value="">Todos los propietarios</option>
              {owners
                .sort((a: any, b: any) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
                .map(o => (
                  <option key={o.id} value={o.id}>{o.firstName} {o.lastName}</option>
                ))}
            </select>
            <select className="input w-auto" value={filterPayment} onChange={e => setFilterPayment(e.target.value as 'all' | 'paid' | 'pending' | 'por_pagar')}>
              <option value="all">Todos los pagos</option>
              <option value="pending">Pendientes de pago</option>
              <option value="por_pagar">Por pagar al propietario</option>
              <option value="paid">Pagadas</option>
            </select>
            {filteredLiquidaciones.length > 0 && (
              <button
                onClick={handleDownloadAll}
                disabled={downloadingAll}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sereno-dark text-sereno-dark text-xs font-semibold hover:bg-sereno-dark hover:text-white disabled:opacity-50 transition-colors ml-auto"
              >
                {downloadingAll ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Descargar todo ({filteredLiquidaciones.length})
              </button>
            )}
          </div>

          {selectedIds.size > 0 && (() => {
            const seleccionadas = liquidaciones.filter(l => selectedIds.has(l.id));
            const pendientes = seleccionadas.filter(estaPendiente);
            // Las que el pago masivo de verdad pagará: pendientes, con saldo y con IBAN
            const pagables = pendientes.filter(l => Number(l.result?.saldo) > 0 && owners.find(o => o.id === l.ownerId)?.iban);
            return (
              <div className="flex items-center gap-3 bg-sereno-dark text-white rounded-xl px-4 py-2.5">
                <span className="text-sm font-medium flex-1">{selectedIds.size} liquidación{selectedIds.size !== 1 ? 'es' : ''} seleccionada{selectedIds.size !== 1 ? 's' : ''}</span>
                <button onClick={() => setSelectedIds(new Set())} className="text-xs text-white/70 hover:text-white transition-colors">Deseleccionar</button>
                {pendientes.length > 0 && (
                  <button
                    onClick={() => abrirMarcarPagada(seleccionadas)}
                    className="flex items-center gap-1.5 text-sm font-semibold text-green-300 hover:text-green-200 transition-colors"
                    title="Registrar un pago hecho fuera de Qonto"
                  >
                    <CheckCircle2 size={14} />
                    Marcar como pagada{pendientes.length !== 1 ? 's' : ''}
                    {pendientes.length !== seleccionadas.length && ` (${pendientes.length})`}
                  </button>
                )}
                {pagables.length > 0 && (
                  <button
                    onClick={() => setPagoMasivo(seleccionadas)}
                    className="flex items-center gap-1.5 text-sm font-semibold text-emerald-300 hover:text-emerald-200 transition-colors"
                    title="Pagar con Qonto, de una en una viendo cada liquidación"
                  >
                    <CreditCard size={14} />
                    Pagar con Qonto{pagables.length !== seleccionadas.length && ` (${pagables.length})`}
                  </button>
                )}
                <button
                  onClick={() => setEnvioMasivo(seleccionadas)}
                  className="flex items-center gap-1.5 text-sm font-semibold text-sky-200 hover:text-sky-100 transition-colors"
                  title="Mandar a cada propietario su liquidación por correo"
                >
                  <Mail size={14} />
                  Enviar por email
                </button>
                {seleccionadas.some(l => l.holdedInvoice?.id && !l.holdedInvoice.adjunto) && (
                  <button
                    onClick={() => setAdjuntoMasivo(seleccionadas)}
                    className="flex items-center gap-1.5 text-sm font-semibold text-amber-200 hover:text-amber-100 transition-colors"
                    title="Adjuntar a cada factura de Holded su liquidación en PDF"
                  >
                    <Paperclip size={14} />
                    Adjuntar en Holded
                  </button>
                )}
                <button onClick={doDeleteBulk} disabled={deletingBulk} className="flex items-center gap-1.5 text-sm font-semibold text-red-300 hover:text-red-200 transition-colors disabled:opacity-50">
                  {deletingBulk ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Eliminar
                </button>
              </div>
            );
          })()}

          {filteredLiquidaciones.length === 0 ? (
            <div className="text-center py-16 text-sereno-dark/40">
              <Receipt className="mx-auto mb-3 opacity-30" size={48} />
              <p className="text-lg font-medium">No hay liquidaciones</p>
              <p className="text-sm mt-1">Crea una nueva liquidación para empezar</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="py-3 px-3 w-8">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 cursor-pointer"
                          checked={filteredLiquidaciones.length > 0 && filteredLiquidaciones.every(l => selectedIds.has(l.id))}
                          ref={el => { if (el) el.indeterminate = filteredLiquidaciones.some(l => selectedIds.has(l.id)) && !filteredLiquidaciones.every(l => selectedIds.has(l.id)); }}
                          onChange={e => {
                            if (e.target.checked) setSelectedIds(new Set(filteredLiquidaciones.map(l => l.id)));
                            else setSelectedIds(new Set());
                          }}
                        />
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-sereno-dark/60">Periodo</th>
                      <th className="text-left py-3 px-4 font-semibold text-sereno-dark/60">Propietario</th>
                      <th className="text-left py-3 px-4 font-semibold text-sereno-dark/60">Modelo</th>
                      <th className="text-right py-3 px-4 font-semibold text-sereno-dark/60">Reservas</th>
                      <th className="text-right py-3 px-4 font-semibold text-sereno-dark/60">Saldo</th>
                      <th className="text-right py-3 px-4 font-semibold text-sereno-dark/60">Pago</th>
                      <th className="text-center py-3 px-2 font-semibold text-sereno-dark/60">Correo</th>
                      <th className="text-right py-3 px-4 font-semibold text-sereno-dark/60">Fecha</th>
                      <th className="text-right py-3 px-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLiquidaciones.map(liq => {
                      const ps = PAYMENT_STATUS[liq.paymentStatus || 'none'] || PAYMENT_STATUS.none;
                      return (
                        <tr key={liq.id} className={classNames("border-b border-gray-50 hover:bg-gray-50 transition-colors", selectedIds.has(liq.id) && "bg-blue-50/60")}>
                          <td className="py-3 px-3">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 cursor-pointer"
                              checked={selectedIds.has(liq.id)}
                              onChange={e => {
                                const next = new Set(selectedIds);
                                if (e.target.checked) next.add(liq.id); else next.delete(liq.id);
                                setSelectedIds(next);
                              }}
                            />
                          </td>
                          <td className="py-3 px-4 font-medium text-sereno-dark whitespace-nowrap" title={liq.periodo}>{periodoCorto(liq)}</td>
                          <td className="py-3 px-4">
                            <OwnerLink ownerId={liq.ownerId} name={liq.ownerName} exists={ownerIds.has(liq.ownerId)} className="text-sereno-dark/80" />
                          </td>
                          <td className="py-3 px-4">
                            <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border', MODEL_COLORS[liq.modelo] || 'bg-gray-50 text-gray-700 border-gray-200')}>
                              {MODEL_LABELS[liq.modelo] || liq.modelo}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">{liq.reservas?.length || 0}</td>
                          <td className={classNames('py-3 px-4 text-right font-semibold', saldoAFavorDeSereno(liq.result?.saldo) ? 'text-amber-700' : 'text-green-700')} title={aFavorDe(liq.result?.saldo)}>{fmt(liq.result?.saldo || 0)}</td>
                          <td className="py-3 px-4 text-right">
                            <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border', ps.className)}>{ps.label}</span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <IconoCorreoLiquidacion liq={liq} vacio={<span className="text-sereno-dark/20" title="Sin enviar">—</span>} />
                          </td>
                          <td className="py-3 px-4 text-right text-sereno-dark/60">{formatDate(liq.createdAt)}</td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => downloadLiqDoc(liq)}
                                disabled={!liq.result}
                                className="p-1.5 rounded-lg hover:bg-sereno-blue/20 text-sereno-dark/50 hover:text-sereno-dark transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                title={`Descargar ${docLabel(liq)}`}
                              >
                                <Download size={16} />
                              </button>
                              <button
                                onClick={() => { openLiqDetail(liq); }}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium bg-sereno-dark text-white hover:bg-sereno-dark/80 transition-colors"
                              >
                                Ver detalle
                              </button>
                              <button
                                onClick={() => doDelete(liq.id)}
                                className="p-1.5 rounded-lg hover:bg-red-50 text-sereno-dark/50 hover:text-red-600 transition-colors"
                                title="Eliminar"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden space-y-3">
                {filteredLiquidaciones.map(liq => {
                  const ps = PAYMENT_STATUS[liq.paymentStatus || 'none'] || PAYMENT_STATUS.none;
                  return (
                    <div key={liq.id} className={classNames("bg-white border border-gray-100 rounded-xl p-4 shadow-sm transition-colors", selectedIds.has(liq.id) && "border-sereno-dark/30 bg-blue-50/30")}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-start gap-2.5">
                          <input
                            type="checkbox"
                            className="mt-0.5 rounded border-gray-300 cursor-pointer shrink-0"
                            checked={selectedIds.has(liq.id)}
                            onChange={e => {
                              const next = new Set(selectedIds);
                              if (e.target.checked) next.add(liq.id); else next.delete(liq.id);
                              setSelectedIds(next);
                            }}
                          />
                          <div>
                            <p>
                              <OwnerLink ownerId={liq.ownerId} name={liq.ownerName} exists={ownerIds.has(liq.ownerId)} className="font-semibold text-sereno-dark" />
                            </p>
                            <p className="text-sm text-sereno-dark/60" title={liq.periodo}>{periodoCorto(liq)}</p>
                          </div>
                        </div>
                        <span className={classNames('text-lg font-bold shrink-0', saldoAFavorDeSereno(liq.result?.saldo) ? 'text-amber-700' : 'text-green-700')} title={aFavorDe(liq.result?.saldo)}>{fmt(liq.result?.saldo || 0)}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border', MODEL_COLORS[liq.modelo] || 'bg-gray-50 text-gray-700 border-gray-200')}>
                          {MODEL_LABELS[liq.modelo] || liq.modelo}
                        </span>
                        <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border', ps.className)}>{ps.label}</span>
                        <span className="text-xs text-sereno-dark/50">{liq.reservas?.length || 0} reservas</span>
                      </div>
                      <div className="flex justify-end gap-1 mt-3">
                        <button
                          onClick={() => downloadLiqDoc(liq)}
                          disabled={!liq.result}
                          className="p-1.5 rounded-lg hover:bg-sereno-blue/20 text-sereno-dark/50 hover:text-sereno-dark transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={`Descargar ${docLabel(liq)}`}
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => { openLiqDetail(liq); }}
                          className="px-2.5 py-1 rounded-lg text-xs font-medium bg-sereno-dark text-white hover:bg-sereno-dark/80 transition-colors"
                        >
                          Ver detalle
                        </button>
                        <button
                          onClick={() => doDelete(liq.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-sereno-dark/50 hover:text-red-600 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {/* ── REMESAS TAB ── */}
      {activeTab === 'remesas' && (
        <>
          {remesaDetail ? (
            /* Remesa detail */
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <button onClick={() => setRemesaDetail(null)} className="text-sm text-sereno-dark/50 hover:text-sereno-dark">
                  ← Volver a remesas
                </button>
                <button
                  onClick={() => { setDeleteRemesaId(remesaDetail.id); setDeleteRemesaAlsoLiqs(false); }}
                  className="flex items-center gap-1.5 text-sm text-sereno-dark/50 hover:text-red-600 transition-colors"
                >
                  <Trash2 size={15} /> Eliminar remesa
                </button>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-sereno-dark">{MONTHS[remesaDetail.month - 1]} {remesaDetail.year}</h2>
                    <p className="text-sm text-sereno-dark/60">{remesaDetail.trigger === 'auto' ? 'Generada automáticamente' : 'Generada manualmente'} · {formatDate(remesaDetail.createdAt)}</p>
                  </div>
                  <div className="flex gap-6 shrink-0">
                    <div className="text-center">
                      <p className="text-xl font-bold text-sereno-dark">{fmt(remesaDetail.summary?.totalSaldo || 0)}</p>
                      <p className="text-xs text-sereno-dark/50 mt-0.5">Total saldo</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-green-700">{remesaDetail.summary?.pagados || 0}</p>
                      <p className="text-xs text-sereno-dark/50 mt-0.5">Pagados</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-amber-600">{remesaDetail.summary?.pendientesPago || 0}</p>
                      <p className="text-xs text-sereno-dark/50 mt-0.5">Pendientes</p>
                    </div>
                  </div>
                </div>
                {remesaDetail.liquidaciones?.length > 0 ? (
                  <div className="space-y-2">
                    {remesaDetail.liquidaciones.map((liq: any) => {
                      const ps = PAYMENT_STATUS[liq.paymentStatus || 'none'] || PAYMENT_STATUS.none;
                      return (
                        <div key={liq.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg gap-3">
                          <div className="min-w-0">
                            <p className="truncate">
                              <OwnerLink ownerId={liq.ownerId} name={liq.ownerName} exists={ownerIds.has(liq.ownerId)} className="font-medium text-sereno-dark text-sm" />
                            </p>
                            <p className="text-xs text-sereno-dark/50" title={liq.periodo}>{periodoCorto(liq)}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={classNames('font-semibold text-sm', saldoAFavorDeSereno(liq.result?.saldo) ? 'text-amber-700' : 'text-sereno-dark')} title={aFavorDe(liq.result?.saldo)}>{fmt(liq.result?.saldo || 0)}</span>
                            <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border', ps.className)}>{ps.label}</span>
                            <IconoCorreoLiquidacion liq={liq} size={15} />
                            <button
                              onClick={() => { openLiqDetail(liq); }}
                              className="px-2.5 py-1 rounded-lg text-xs font-medium bg-sereno-dark text-white hover:bg-sereno-dark/80 transition-colors"
                            >
                              Ver detalle
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-sereno-dark/50 py-4 text-center">Sin liquidaciones en esta remesa</p>
                )}
              </div>
            </div>
          ) : (
            /* Remesas list */
            <>
              <div className="flex flex-wrap items-center gap-3">
                <select
                  className="input w-auto"
                  value={remesaFilterYear}
                  onChange={e => { const y = Number(e.target.value); setRemesaFilterYear(y); loadRemesas(y); }}
                >
                  {[2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              {loadingRemesas ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="animate-spin text-sereno-dark/40" size={32} />
                </div>
              ) : remesas.length === 0 ? (
                <div className="text-center py-16 text-sereno-dark/40">
                  <Layers className="mx-auto mb-3 opacity-30" size={48} />
                  <p className="text-lg font-medium">No hay remesas</p>
                  <p className="text-sm mt-1">Genera una remesa mensual para agrupar las liquidaciones</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {remesas.map(r => (
                    <div key={r.id} className="relative group bg-white border border-gray-100 rounded-xl shadow-sm hover:border-sereno-dark/20 transition-colors">
                      <button
                        onClick={() => openRemesaDetail(r.id)}
                        className="w-full text-left p-4"
                      >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pr-8">
                        <div>
                          <p className="font-semibold text-sereno-dark">{MONTHS[r.month - 1]} {r.year}</p>
                          <p className="text-sm text-sereno-dark/50">{r.summary?.totalLiquidaciones ?? r.liquidacionIds?.length ?? 0} liquidacion{(r.summary?.totalLiquidaciones ?? r.liquidacionIds?.length ?? 0) !== 1 ? 'es' : ''} · {r.trigger === 'auto' ? 'Auto' : 'Manual'}</p>
                        </div>
                        <div className="flex items-center flex-wrap gap-3 text-sm">
                          <div>
                            <span className="font-bold text-sereno-dark">{fmt(r.summary?.totalSaldo || 0)}</span>
                            <span className="text-sereno-dark/50 ml-1">total</span>
                          </div>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border bg-green-50 text-green-700 border-green-200">
                            <CheckCircle2 size={10} /> {r.summary?.pagados || 0} pagados
                          </span>
                          {(r.summary?.pendientesPago || 0) > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-amber-50 text-amber-700 border-amber-200">
                              {r.summary?.pendientesPago} pendientes
                            </span>
                          )}
                        </div>
                      </div>
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); setDeleteRemesaId(r.id); setDeleteRemesaAlsoLiqs(false); }}
                        className="absolute top-3 right-3 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-50 text-sereno-dark/40 hover:text-red-600 transition-all"
                        title="Eliminar remesa"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── GASTOS SIN PRECIO TAB ── */}
      {activeTab === 'pendingPricing' && (
        <div className="space-y-4">
          {loadingPending ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-sereno-dark/40" size={32} />
            </div>
          ) : pendingExpenses.length === 0 ? (
            <div className="text-center py-16 flex flex-col items-center gap-3">
              <CheckCircle className="text-green-500" size={40} />
              <p className="text-sereno-dark/60 font-medium">No hay gastos pendientes de precio</p>
              <p className="text-sereno-dark/40 text-sm">Todos los gastos tienen precio asignado</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-sereno-dark/60">
                {pendingExpenses.length} gasto{pendingExpenses.length !== 1 ? 's' : ''} pendiente{pendingExpenses.length !== 1 ? 's' : ''} de asignar precio. Estos gastos no se incluirán en remesas hasta que tengan precio.
              </p>

              {/* Desktop table */}
              <div className="hidden sm:block bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="py-3 px-4 text-left font-semibold text-sereno-dark/70">Propiedad</th>
                      <th className="py-3 px-4 text-left font-semibold text-sereno-dark/70">Concepto</th>
                      <th className="py-3 px-4 text-left font-semibold text-sereno-dark/70">Categoría</th>
                      <th className="py-3 px-4 text-left font-semibold text-sereno-dark/70">Fecha</th>
                      <th className="py-3 px-4 text-right font-semibold text-sereno-dark/70">Precio (€)</th>
                      <th className="py-3 px-4 text-right font-semibold text-sereno-dark/70" colSpan={2}>Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {pendingExpenses.map(exp => (
                      <tr key={exp.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-4 font-medium text-sereno-dark">{exp.propertyName}</td>
                        <td className="py-3 px-4 text-sereno-dark/80">{exp.concept}</td>
                        <td className="py-3 px-4">
                          <span className={classNames(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border',
                            exp.source === 'laundry'
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-orange-50 text-orange-700 border-orange-200',
                          )}>
                            <Tag size={10} />
                            {exp.source === 'laundry' ? 'Lavandería' : exp.category || 'Mantenimiento'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sereno-dark/60">{formatDate(exp.date)}</td>
                        <td className="py-3 px-4">
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            placeholder="0,00"
                            value={expensePrices[exp.id] || ''}
                            onChange={e => setExpensePrices(prev => ({ ...prev, [exp.id]: e.target.value }))}
                            className="w-28 text-right border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sereno-dark/20 focus:border-sereno-dark"
                          />
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => saveExpensePrice(exp)}
                            disabled={savingExpenseId === exp.id || !expensePrices[exp.id] || parseFloat(expensePrices[exp.id]) <= 0}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-sereno-dark text-white hover:bg-sereno-dark/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5 ml-auto"
                          >
                            {savingExpenseId === exp.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                            Guardar
                          </button>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => doArchiveExpense(exp)}
                            disabled={archivingExpenseId === exp.id}
                            title="Archivar (ocultar del CRM sin eliminar)"
                            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-sereno-dark/60 hover:bg-gray-100 hover:text-sereno-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5 ml-auto"
                          >
                            {archivingExpenseId === exp.id ? <Loader2 size={13} className="animate-spin" /> : <Archive size={13} />}
                            Archivar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden space-y-3">
                {pendingExpenses.map(exp => (
                  <div key={exp.id} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-sereno-dark text-sm">{exp.propertyName}</p>
                        <p className="text-xs text-sereno-dark/60 mt-0.5">{exp.concept}</p>
                      </div>
                      <span className={classNames(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border shrink-0',
                        exp.source === 'laundry'
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : 'bg-orange-50 text-orange-700 border-orange-200',
                      )}>
                        <Tag size={10} />
                        {exp.source === 'laundry' ? 'Lavandería' : exp.category || 'Mantenimiento'}
                      </span>
                    </div>
                    <p className="text-xs text-sereno-dark/50">{formatDate(exp.date)}</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="0,00"
                        value={expensePrices[exp.id] || ''}
                        onChange={e => setExpensePrices(prev => ({ ...prev, [exp.id]: e.target.value }))}
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sereno-dark/20 focus:border-sereno-dark"
                      />
                      <button
                        onClick={() => saveExpensePrice(exp)}
                        disabled={savingExpenseId === exp.id || !expensePrices[exp.id] || parseFloat(expensePrices[exp.id]) <= 0}
                        className="px-3 py-2 rounded-lg text-sm font-medium bg-sereno-dark text-white hover:bg-sereno-dark/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                      >
                        {savingExpenseId === exp.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                        Guardar
                      </button>
                      <button
                        onClick={() => doArchiveExpense(exp)}
                        disabled={archivingExpenseId === exp.id}
                        title="Archivar (ocultar del CRM sin eliminar)"
                        className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 text-sereno-dark/60 hover:bg-gray-100 hover:text-sereno-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                      >
                        {archivingExpenseId === exp.id ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />}
                        Archivar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Delete remesa confirmation modal */}
      {deleteRemesaId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full space-y-4">
            <h3 className="text-lg font-bold text-sereno-dark">Eliminar remesa</h3>
            <p className="text-sm text-sereno-dark/60">
              Esta acción no se puede deshacer. La remesa se eliminará permanentemente.
            </p>
            <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={deleteRemesaAlsoLiqs}
                onChange={e => setDeleteRemesaAlsoLiqs(e.target.checked)}
              />
              <div>
                <p className="text-sm font-medium text-sereno-dark">Eliminar también las liquidaciones</p>
                <p className="text-xs text-sereno-dark/50 mt-0.5">Borrará todas las liquidaciones asociadas a esta remesa</p>
              </div>
            </label>
            <div className="flex gap-3 pt-1">
              <button onClick={() => { setDeleteRemesaId(null); setDeleteRemesaAlsoLiqs(false); }} disabled={deletingRemesa} className="btn-ghost flex-1">Cancelar</button>
              <button onClick={doDeleteRemesa} disabled={deletingRemesa} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors">
                {deletingRemesa ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate remesa modal */}
      {showGenerateRemesa && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full space-y-4">
            <h3 className="text-lg font-bold text-sereno-dark">Generar remesa</h3>
            <p className="text-sm text-sereno-dark/60">
              Se generará una liquidación automática para todos los propietarios activos con reservas en el período indicado.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Mes</label>
                <select className="input" value={remesaMonth} onChange={e => setRemesaMonth(Number(e.target.value))} disabled={generatingRemesa}>
                  {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Año</label>
                <select className="input" value={remesaYear} onChange={e => setRemesaYear(Number(e.target.value))} disabled={generatingRemesa}>
                  {[2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            {/* Progress bar */}
            {remesaProgress && (
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-sereno-dark/60">
                  <span className="truncate max-w-[200px]">{remesaProgress.ownerName}</span>
                  <span className="shrink-0 font-medium">{remesaProgress.current} / {remesaProgress.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-sereno-dark h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.max(4, Math.round((remesaProgress.current / remesaProgress.total) * 100))}%` }}
                  />
                </div>
              </div>
            )}
            {/* Warning: skipped zero-price expenses */}
            {remesaSkippedExpenses > 0 && (
              <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
                <AlertTriangle size={15} className="shrink-0" />
                <span>{remesaSkippedExpenses} gasto{remesaSkippedExpenses !== 1 ? 's' : ''} excluido{remesaSkippedExpenses !== 1 ? 's' : ''} por no tener precio asignado</span>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button onClick={() => { setShowGenerateRemesa(false); setRemesaProgress(null); setRemesaSkippedExpenses(0); }} disabled={generatingRemesa} className="btn-ghost flex-1">Cancelar</button>
              <button
                onClick={doGenerateRemesa}
                disabled={generatingRemesa}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {generatingRemesa ? <Loader2 className="animate-spin" size={16} /> : <Layers size={16} />}
                {generatingRemesa ? 'Generando...' : 'Generar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading remesa detail overlay */}
      {loadingRemesaDetail && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 shadow-xl flex items-center gap-3">
            <Loader2 className="animate-spin text-sereno-dark" size={24} />
            <span className="text-sereno-dark font-medium">Cargando remesa...</span>
          </div>
        </div>
      )}

      {markPaidModal}

      {pagoMasivo && (
        <PagoMasivo
          liquidaciones={pagoMasivo}
          owners={owners}
          generarPdf={pdfDocBlob}
          onLiquidacionActualizada={aplicarLiqActualizada}
          onCerrar={() => setPagoMasivo(null)}
        />
      )}

      {adjuntoMasivo && (
        <AdjuntoMasivo
          liquidaciones={adjuntoMasivo}
          generarPdf={pdfLiquidacionBase64}
          onLiquidacionActualizada={aplicarLiqActualizada}
          onCerrar={() => setAdjuntoMasivo(null)}
        />
      )}

      {envioMasivo && (
        <EnvioMasivo
          liquidaciones={envioMasivo}
          owners={owners}
          generarPdf={pdfLiquidacionBase64}
          onLiquidacionActualizada={aplicarLiqActualizada}
          onCerrar={() => setEnvioMasivo(null)}
        />
      )}
    </div>
  );
}
